/**
 * AssignedTaskDetailPage — deep-view of a single assigned task.
 *
 * Route: /dashboard/cloudgm/assigned-tasks/:id
 *
 * Switch button at the top toggles between:
 *   1) الطلب (Request)      — shows request info + stage timeline
 *      Stages (agent tasks only):
 *        قيد المراجعة  (locked, auto-done)
 *        تمت الموافقة  (locked, auto-done)
 *        قيد التنفيذ  (toggleable)
 *        تم الإنجاز   (toggleable)
 *        تحصيل العمولة (toggleable)
 *        تم إرسال العمولة (auto — set when a commission payment is recorded)
 *        أغلق الطلب   (toggleable — must be done to submit for review)
 *
 *   2) الإجراءات (Actions) — internal-only executor steps
 *      Each row: description + optional amount + optional attachments
 *      Optionally: transfer the task to another staff member
 *      Total amount auto-sums from all actions.
 *      Bottom: "إرسال للإغلاق والتدقيق" button — enabled ONLY when
 *      the 'closed' stage is marked done in the Request tab.
 */
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Loader2, User, Store, Clock, CheckCircle2, Wallet, Send, Package,
  Lock, ClipboardCheck, Plus, Paperclip, FileText, Image as ImageIcon, X,
  AlertCircle, ClipboardList, ScrollText, ArrowRightLeft, Coins, GitBranch,
  CheckCircle, Users, Bot, MessageCircle, ChevronDown, ChevronUp,
  XCircle, Trash2, Printer, Activity, Pencil, Check, Receipt as ReceiptIcon,
} from 'lucide-react';

import DashboardHeader from '../../components/DashboardHeader';
import BreadcrumbNav from '../../components/BreadcrumbNav';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { getAuthHeaders } from '../../utils/authHeaders';
import { printAssignedTask } from '../../utils/printAssignedTask';
import { printReceipt } from '../../utils/printReceipt';
import { floorAED, fmtAED } from '../../utils/money';
import { DirhamPrice } from '../../components/DirhamPrice';
import { openAttachment } from '../../utils/openAttachment';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Stage config — order + labels + icon. First two are locked by default.
// iter-190 (Feb 2026) — Added `fees_collected` stage between `in_progress`
// and `completed`. When done, the FeesSettlementCard locks. Toggling OFF
// unlocks it again.
const STAGE_CONFIG = [
  { key: 'pending_review',       ar: 'قيد المراجعة',    en: 'Pending review',       Icon: Clock },
  { key: 'approved',             ar: 'تمت الموافقة',   en: 'Approved',              Icon: CheckCircle2 },
  { key: 'in_progress',          ar: 'قيد التنفيذ',    en: 'In progress',           Icon: Send },
  { key: 'fees_collected',       ar: 'تحصيل الرسوم',   en: 'Fees collected',        Icon: Wallet },
  { key: 'completed',            ar: 'تم الإنجاز',     en: 'Completed',             Icon: Package },
  { key: 'commission_collected', ar: 'تحصيل العمولة',  en: 'Commission collected',  Icon: Coins },
  { key: 'commission_sent',      ar: 'تم إرسال العمولة', en: 'Commission sent',     Icon: Send },
  { key: 'closed',               ar: 'تم الانتهاء وإغلاق الطلب', en: 'Completed & Closed', Icon: Lock },
];

const MAX_ATT = 8;
const MAX_ATT_MB = 8;

export default function AssignedTaskDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { user } = useAuth();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);

  // iter-218 (Feb 2026) — Super-admin identifier. Only the primary admin
  // (admin@asb606.com) may hard-delete a task after it has been closed &
  // finalised. Staff and secondary admins lose the button once the task
  // enters `review_status='closed'`.
  // iter-246 (Feb 2026) — Delete button is now ADMIN-ONLY (per user's rule:
  // "الحذف يكون حصراً للـ admin"). We keep the `isSuperAdmin` alias for
  // the existing "delete after finalised" toggle downstream. Non-admin
  // roles (staff / agent / accountant) never see the button.
  const isAdminRole  = String(user?.role || '').toLowerCase() === 'admin';
  const isSuperAdmin = isAdminRole && (user?.email === 'admin@asb606.com' || user?.username === 'admin');

  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('request');   // 'request' | 'actions'
  const [staff, setStaff] = useState([]);
  // Feb 2026 — iter-182: cancel modal state (must live above any early return)
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  // Load task
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/assigned-tasks/${id}`, {
        headers: getAuthHeaders(),
      });
      setTask(r.data);
    } catch {
      toast.error(t('فشل التحميل', 'Load failed'));
    } finally {
      setLoading(false);
    }
  }, [id, isAr]);

  useEffect(() => { load(); }, [load]);

  // Load staff (for transfer picker)
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/admin/assigned-tasks/staff`, {
          headers: getAuthHeaders(),
        });
        setStaff(r.data.data || []);
      } catch { /* non-fatal */ }
    })();
  }, []);

  if (loading || !task) {
    return (
      <div className="min-h-screen bg-[#030303]">
        <DashboardHeader showBackButton backPath="/dashboard" />
        <div className="flex items-center justify-center py-32">
          <Loader2 className="w-8 h-8 animate-spin text-[#d4af37]" />
        </div>
      </div>
    );
  }

  const isCustomer = task.target_type === 'customer';
  const accent = isCustomer ? '#3b82f6' : '#8b5cf6';
  const TargetIcon = isCustomer ? User : Store;
  const stages = task.request_stages || {};
  const closedDone = (stages.closed || {}).done === true;
  const isFinalised = task.review_status === 'closed';
  const isReadyForAudit = task.review_status === 'ready_for_review';

  const toggleStage = async (stageKey, done) => {
    if (isFinalised) {
      toast.error(t('المهمة مغلقة ومدققة — لا يمكن التعديل', 'Task is finalised — no changes allowed'));
      return;
    }
    const stageInfo = stages[stageKey] || {};
    if (stageInfo.locked) {
      toast.error(t('هذه المرحلة مقفلة ولا يمكن تعديلها', 'This stage is locked'));
      return;
    }
    try {
      const r = await axios.patch(
        `${API_URL}/api/admin/assigned-tasks/${task.id}/stage`,
        { stage: stageKey, done },
        { headers: getAuthHeaders() },
      );
      setTask(r.data);
      if (stageKey === 'closed' && done) {
        toast.success(t('انتقلت المهمة إلى قائمة المنجزة والجاهزة للتدقيق', 'Task moved to "Ready for audit" bucket'));
      } else {
        toast.success(t('تم تحديث المرحلة', 'Stage updated'));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشل التحديث', 'Update failed'));
    }
  };

  // iter-202 (Feb 2026) — Retro-edit a stage's `at` timestamp.
  // Used by StageDateEditor to persist a backdated timestamp on a completed
  // stage. The SR mirror is refreshed by the backend automatically.
  const patchStageDate = async (stageKey, newIso) => {
    if (isFinalised) {
      toast.error(t('المهمة مغلقة ومدققة — لا يمكن التعديل', 'Task is finalised — no changes allowed'));
      return false;
    }
    try {
      const r = await axios.patch(
        `${API_URL}/api/admin/assigned-tasks/${task.id}/stage-date`,
        { stage: stageKey, at: newIso },
        { headers: getAuthHeaders() },
      );
      setTask(r.data);
      toast.success(t('تم تحديث تاريخ المرحلة', 'Stage date updated'));
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشل تحديث التاريخ', 'Date update failed'));
      return false;
    }
  };

  const closeAfterAudit = async () => {
    if (!window.confirm(t('تأكيد الإغلاق النهائي بعد التدقيق؟ لن يمكن التعديل بعد ذلك.',
                          'Confirm final close after audit? No further edits will be allowed.'))) return;
    try {
      const r = await axios.post(
        `${API_URL}/api/admin/assigned-tasks/${task.id}/close-audit`,
        {},
        { headers: getAuthHeaders() },
      );
      setTask(r.data);
      toast.success(t('تم الإغلاق النهائي والتدقيق', 'Task finalised & audited'));
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشل الإغلاق', 'Close failed'));
    }
  };

  // Feb 2026 — iter-182: Cancel (soft) + Delete (hard) — the two options
  // requested by the admin. Cancel writes a reason and mirrors to
  // service_requests so the customer/merchant platform sees it too.
  const submitCancel = async () => {
    if (!cancelReason.trim()) {
      toast.error(t('اكتب سبب الإلغاء', 'Type the cancellation reason'));
      return;
    }
    setCancelling(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/admin/assigned-tasks/${task.id}/cancel`,
        { reason: cancelReason.trim(), reason_ar: cancelReason.trim(), reason_en: cancelReason.trim() },
        { headers: getAuthHeaders() },
      );
      setTask(r.data);
      setCancelOpen(false);
      setCancelReason('');
      toast.success(t('تم إلغاء الطلب — سيظهر عند العميل/التاجر مُلغىً', 'Cancelled — will show as cancelled on the customer/merchant side'));
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشل الإلغاء', 'Cancel failed'));
    } finally {
      setCancelling(false);
    }
  };

  const deleteTaskHard = async () => {
    if (!window.confirm(t(
      '⚠️ حذف نهائي للطلب من النظام — لا يمكن التراجع. متابعة؟',
      '⚠️ Hard-delete this task — this cannot be undone. Continue?'
    ))) return;
    try {
      await axios.delete(
        `${API_URL}/api/admin/assigned-tasks/${task.id}`,
        { headers: getAuthHeaders() },
      );
      toast.success(t('تم الحذف نهائياً', 'Deleted permanently'));
      navigate(-1);
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشل الحذف', 'Delete failed'));
    }
  };

  // iter-218 (Feb 2026) — Receipt handlers.
  // Creates (or reuses via backend idempotency) a CGB-YYYY-XXXXXXX receipt
  // for this task, then renders the print / download flow.
  // iter-218b — Snapshots the payment collection date + method + sub-service
  // + payer contact info (from requestor fields for agent tasks, target
  // fields for customer tasks — SAME logic as FeesSettlementCard so the
  // person on the receipt matches the person that paid).
  const fetchOrCreateReceipt = async () => {
    const payments = task.payments || [];
    const paidTotal = payments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
    const totalDue  = Number(task.total_price || 0);
    if (!(totalDue > 0 && paidTotal >= totalDue - 0.005)) {
      toast.error(t(
        'الإيصال متاح فقط بعد سداد الرسوم بالكامل',
        'Receipts are only available after fees are fully paid',
      ));
      return null;
    }
    // iter-218c (Feb 2026) — Payer info comes STRICTLY from the requestor
    // card ("بيانات المقدم لأجل هذا الطلب") for agent tasks — never fall back
    // to the agency (target) name/phone/email. If a field is empty on the
    // requestor card, leave it empty on the receipt.
    const isAgentTask = task.target_type === 'agent';
    const payerName  = isAgentTask
      ? (task.requestor_name  || '—')   // NO fallback to agency name
      : (task.target_name  || '—');
    const payerPhone = isAgentTask
      ? (task.requestor_phone || '')   // NO fallback to agency phone — leave empty
      : (task.target_phone || '');
    const payerEmail = isAgentTask
      ? (task.requestor_email || '')   // NO fallback to agency email — leave empty
      : (task.target_email || '');
    // Collection date: prefer `fees_collected_at` (stage stamp) then last
    // payment's `created_at` as fallback.
    const lastPaymentAt = payments.length
      ? (payments[payments.length - 1]?.created_at || '')
      : '';
    const paidAt = task.fees_collected_at || lastPaymentAt || null;
    // Payment method — first payment wins (usually a single method per task).
    const paymentMethod = (payments[0]?.payment_method) || 'bank_transfer';
    try {
      const r = await axios.post(
        `${API_URL}/api/admin/receipts`,
        {
          task_id:             task.id,
          payer_name:          payerName,
          payer_phone:         payerPhone || null,
          payer_email:         payerEmail || null,
          request_no:          task.request_no_display || task.id,
          amount_aed:          paidTotal,
          currency:            task.currency || 'AED',
          paid_at:             paidAt,
          payment_method:      paymentMethod,
          service_name_ar:     task.service_name_ar || null,
          service_name_en:     task.service_name_en || null,
          sub_service_name_ar: task.sub_service_name_ar || null,
          sub_service_name_en: task.sub_service_name_en || null,
        },
        { headers: getAuthHeaders() },
      );
      // iter-218c (Feb 2026) — Backend returns cached receipt if one already
      // exists for this task. We override the payer info + sub-service on the
      // client so the PRINTED receipt always reflects the LIVE requestor
      // card, even for older receipts created before the strict-requestor
      // logic landed. The DB revenue row stays untouched (immutable amounts,
      // dates, receipt_no).
      return {
        ...r.data,
        payer_name:          payerName,
        payer_phone:         payerPhone || null,
        payer_email:         payerEmail || null,
        sub_service_name_ar: task.sub_service_name_ar || r.data.sub_service_name_ar || null,
        sub_service_name_en: task.sub_service_name_en || r.data.sub_service_name_en || null,
        service_name_ar:     task.service_name_ar || r.data.service_name_ar || null,
        service_name_en:     task.service_name_en || r.data.service_name_en || null,
      };
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشل إنشاء الإيصال', 'Failed to create receipt'));
      return null;
    }
  };
  const handlePrintReceipt = async (lang) => {
    const rc = await fetchOrCreateReceipt();
    if (!rc) return;
    // iter-243 — Manual tasks reach the receipt stage only after full
    // collection, so the stamp is always "PAID".
    try { printReceipt({ ...rc, status: 'paid' }, { language: lang }); }
    catch { toast.error(t('فشل فتح الطباعة', 'Print failed')); }
  };

  return (
    <div className="min-h-screen bg-[#030303]" dir={isAr ? 'rtl' : 'ltr'} data-testid="assigned-task-detail-page">
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] rounded-full blur-[150px]"
             style={{ backgroundColor: `${accent}10` }} />
      </div>

      {/* Feb 2026: back-path depends on where the task belongs — customer
          tasks go back to منصة العملاء ▸ طلبات الخدمة, agent tasks go back to
          منصة التجار ▸ متابعة مهام الوكلاء. */}
      <DashboardHeader showBackButton backPath={
        task.target_type === 'agent'
          ? '/manage/cloudgm-business?tab=follow-agent-tasks'
          : '/manage/cloudgm?tab=services-categories'
      } />

      <div className="border-b border-white/5">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <BreadcrumbNav items={
            task.target_type === 'agent'
              ? [
                  { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
                  { label_ar: 'CLOUDGM FZ LLC', label_en: 'CLOUDGM FZ LLC', path: '/dashboard?view=platform-control&sub=fzlc' },
                  { label_ar: 'منصة التجار', label_en: 'Merchants Platform', path: '/manage/cloudgm-business' },
                  { label_ar: 'مهام الوكلاء', label_en: 'Agent Tasks', path: '/manage/cloudgm-business?tab=agent-tasks' },
                  { label_ar: 'متابعة مهام الوكلاء', label_en: 'Track Agent Tasks', path: '/manage/cloudgm-business?tab=follow-agent-tasks' },
                  { label_ar: task.request_no_display || 'تفاصيل', label_en: task.request_no_display || 'Detail', path: null },
                ]
              : [
                  { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
                  { label_ar: 'CLOUDGM FZ LLC', label_en: 'CLOUDGM FZ LLC', path: '/dashboard?view=platform-control&sub=fzlc' },
                  { label_ar: 'منصة العملاء', label_en: 'Customers Platform', path: '/manage/cloudgm' },
                  { label_ar: 'طلبات الخدمة', label_en: 'Service Requests', path: '/manage/cloudgm?tab=services-categories' },
                  { label_ar: task.request_no_display || 'تفاصيل', label_en: task.request_no_display || 'Detail', path: null },
                ]
          } />
        </div>
      </div>

      <main className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">

        {/* Header card */}
        <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-5 mb-5">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                 style={{ backgroundColor: `${accent}15`, border: `1px solid ${accent}55` }}>
              <TargetIcon className="w-7 h-7" style={{ color: accent }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                {task.request_no_display && (
                  <span className="text-xs font-mono px-2 py-0.5 rounded-md border"
                        style={{ backgroundColor: `${accent}20`, borderColor: `${accent}40`, color: accent }}>
                    {task.request_no_display}
                  </span>
                )}
                {task.source === 'ai_generated' && (
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-200 border border-purple-500/40 inline-flex items-center gap-1">
                    <Bot className="w-3 h-3" />
                    {t('من الذكاء الاصطناعي', 'AI generated')}
                  </span>
                )}
                <span className="text-xs text-white/40">{new Date(task.created_at).toLocaleString(isAr ? 'ar-EG' : 'en-GB')}</span>
                {task.review_status === 'ready_for_review' && (
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#d4af37]/20 text-[#d4af37] border border-[#d4af37]/40">
                    {t('جاهزة للتدقيق', 'Ready for audit')}
                  </span>
                )}
                {task.review_status === 'closed' && (
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                    {t('مغلقة نهائياً', 'Finalised')}
                  </span>
                )}
              </div>
              <h1 className="text-xl md:text-2xl font-bold text-white">
                {isAr ? task.service_name_ar : (task.service_name_en || task.service_name_ar)}
              </h1>
              {/* iter-218b (Feb 2026) — Sub-service is stored on the task
                  (sub_service_name_ar/en) but wasn't rendered in the header.
                  Now shown as an amber pill under the main service name. */}
              {(task.sub_service_name_ar || task.sub_service_name_en) && (
                <div className="mt-1.5 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-200 text-[11px] font-semibold"
                     data-testid="task-sub-service-pill">
                  <Package className="w-3 h-3" />
                  {t('الخدمة الفرعية', 'Sub-service')}:
                  <span className="text-amber-100">
                    {isAr ? (task.sub_service_name_ar || task.sub_service_name_en) : (task.sub_service_name_en || task.sub_service_name_ar)}
                  </span>
                </div>
              )}
              <div className="text-sm text-white/60 mt-1">
                {task.target_type === 'agent'
                  ? t('الوكيل', 'Agent')
                  : t('المستهدف', 'Target')}
                : <span className="text-white">{task.target_name}</span>
                {task.target_phone && <span className="ms-3 font-mono text-white/40" dir="ltr">{task.target_phone}</span>}
                {task.target_email && <span className="ms-3 text-white/40" dir="ltr">{task.target_email}</span>}
              </div>
              {task.assigned_to_name && (
                <div className="text-xs text-white/50 mt-1">
                  {t('منفذ المهمة الحالي', 'Current executor')}: <span className="text-white/80">{task.assigned_to_name}</span>
                </div>
              )}
            </div>

            {/* iter-217c-v15 (Feb 2026) — TWO separate buttons per user's
                explicit request:
                  1) طباعة  → opens native print dialog (physical printer or
                     Save as PDF is the user's own choice).
                  2) تحميل PDF → REAL download via html2pdf.js. File named
                     `بيانات_المقدم_{name}_طلب_{no}.pdf` (no agent name,
                     no `-ar/-en` suffix).
                Each button has ONLY Arabic / English (no combined).
                Hover-menu bridge: menu has `pt-2` internal padding instead of
                `mt-2` external margin, so the mouse never crosses an empty
                gap where hover would lose. */}
            <div className="relative group" data-testid="print-btn-wrap">
              <Button
                variant="outline"
                className="border-white/15 bg-white/[0.03] text-white hover:bg-white/[0.06] hover:text-[#d4af37] hover:border-[#d4af37]/40 rounded-xl font-semibold"
                data-testid="print-pdf-btn"
                title={t('طباعة الطلب', 'Print the request')}
              >
                <Printer className="w-4 h-4 me-1.5" />
                {t('طباعة', 'Print')}
              </Button>
              <div className="absolute end-0 top-full pt-2 min-w-[180px] opacity-0 invisible group-hover:opacity-100 group-hover:visible focus-within:opacity-100 focus-within:visible transition z-30" data-testid="print-lang-menu">
                <div className="rounded-xl border border-white/10 bg-[#0a0a0a] shadow-2xl overflow-hidden">
                  <button
                    type="button"
                    onClick={() => {
                      try { printAssignedTask(task, { language: 'ar' }); }
                      catch { toast.error(t('فشل فتح الطباعة', 'Print failed')); }
                    }}
                    className="w-full text-start px-4 py-2.5 text-sm text-white/90 hover:bg-white/5 flex items-center gap-2"
                    data-testid="print-lang-ar"
                  >
                    <span className="text-[10px] font-bold text-emerald-300 bg-emerald-500/15 border border-emerald-500/30 rounded px-1.5 py-0.5">AR</span>
                    {isAr ? 'طباعة بالعربية' : 'Print in Arabic'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      try { printAssignedTask(task, { language: 'en' }); }
                      catch { toast.error(t('فشل فتح الطباعة', 'Print failed')); }
                    }}
                    className="w-full text-start px-4 py-2.5 text-sm text-white/90 hover:bg-white/5 flex items-center gap-2 border-t border-white/5"
                    data-testid="print-lang-en"
                  >
                    <span className="text-[10px] font-bold text-sky-300 bg-sky-500/15 border border-sky-500/30 rounded px-1.5 py-0.5">EN</span>
                    {isAr ? 'طباعة بالإنجليزية' : 'Print in English'}
                  </button>
                </div>
              </div>
            </div>

            {/* iter-218c (Feb 2026) — "تحميل PDF" button REMOVED at user's
                explicit request: downloads were not rendering correctly, so
                only the native "طباعة" flow is kept (user can Save-as-PDF
                from the print dialog if needed). */}

            {/* iter-218 (Feb 2026) — Receipt (Payment receipt) button.
                Shows for manual assigned tasks. Behind the scenes it POSTs
                to /api/admin/receipts (idempotent per task_id) then renders
                the receipt PDF. Available only after fees are fully paid;
                clicking before that shows a friendly toast. */}
            <div className="relative group" data-testid="receipt-btn-wrap">
              <Button
                variant="outline"
                className="border-emerald-500/40 bg-emerald-500/[0.06] text-emerald-200 hover:bg-emerald-500/[0.12] hover:text-emerald-100 hover:border-emerald-500/60 rounded-xl font-semibold"
                data-testid="receipt-btn"
                title={t('طباعة إيصال قبض', 'Print payment receipt')}
              >
                <ReceiptIcon className="w-4 h-4 me-1.5" />
                {t('الإيصال', 'Receipt')}
              </Button>
              <div className="absolute end-0 top-full pt-2 min-w-[220px] opacity-0 invisible group-hover:opacity-100 group-hover:visible focus-within:opacity-100 focus-within:visible transition z-30" data-testid="receipt-menu">
                <div className="rounded-xl border border-white/10 bg-[#0a0a0a] shadow-2xl overflow-hidden">
                  <button
                    type="button"
                    onClick={() => handlePrintReceipt('ar')}
                    className="w-full text-start px-4 py-2.5 text-sm text-white/90 hover:bg-white/5 flex items-center gap-2"
                    data-testid="receipt-print-ar"
                  >
                    <Printer className="w-3.5 h-3.5 text-emerald-300" />
                    <span className="text-[10px] font-bold text-emerald-300 bg-emerald-500/15 border border-emerald-500/30 rounded px-1.5 py-0.5">AR</span>
                    {t('طباعة بالعربية', 'Print in Arabic')}
                  </button>
                  <button
                    type="button"
                    onClick={() => handlePrintReceipt('en')}
                    className="w-full text-start px-4 py-2.5 text-sm text-white/90 hover:bg-white/5 flex items-center gap-2 border-t border-white/5"
                    data-testid="receipt-print-en"
                  >
                    <Printer className="w-3.5 h-3.5 text-sky-300" />
                    <span className="text-[10px] font-bold text-sky-300 bg-sky-500/15 border border-sky-500/30 rounded px-1.5 py-0.5">EN</span>
                    {t('طباعة بالإنجليزية', 'Print in English')}
                  </button>
                  {/* iter-218c (Feb 2026) — "تحميل PDF" options REMOVED at
                      user's explicit request: downloads were not rendering
                      correctly for receipts, only Print is kept. */}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Feb 2026 (iter-184) — Prominent status hero: shows Active/Cancelled/Finalised
             + current stage at a glance, with a colour-coded pill. */}
        <StatusHero task={task} t={t} isAr={isAr} />

        {/* Finalised banner */}
        {isFinalised && (
          <div className="mb-5 bg-emerald-500/10 border border-emerald-500/40 rounded-2xl p-4 flex items-start gap-3" data-testid="finalised-banner">
            <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
              <Lock className="w-5 h-5 text-emerald-300" />
            </div>
            <div>
              <div className="font-bold text-emerald-300 text-sm">
                {t('هذه المهمة مغلقة ومدققة نهائياً', 'This task is finalised (closed & audited)')}
              </div>
              <div className="text-xs text-emerald-300/70 mt-0.5">
                {t('لا يمكن التعديل أو إضافة إجراءات أو تغيير المراحل بعد الآن.',
                   'No edits, actions, or stage changes are allowed anymore.')}
                {task.review_closed_at && (
                  <span className="ms-2 text-white/40" dir="ltr">
                    ({new Date(task.review_closed_at).toLocaleString(isAr ? 'ar-EG' : 'en-GB')})
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Feb 2026 — iter-182: Cancelled banner */}
        {task.status === 'cancelled' && (
          <div className="mb-5 bg-red-500/10 border border-red-500/40 rounded-2xl p-4" data-testid="cancelled-banner">
            <div className="font-bold text-red-300 text-sm">
              {t('الطلب مُلغى', 'Request cancelled')}
              {task.cancelled_at && (
                <span className="ms-2 text-white/40 text-xs" dir="ltr">
                  ({new Date(task.cancelled_at).toLocaleString(isAr ? 'ar-EG' : 'en-GB')})
                </span>
              )}
            </div>
            {task.cancel_reason && (
              <div className="text-xs text-red-200/80 mt-1">
                {t('السبب:', 'Reason:')} {isAr ? (task.cancel_reason_ar || task.cancel_reason) : (task.cancel_reason_en || task.cancel_reason)}
              </div>
            )}
          </div>
        )}

        {/* Feb 2026 — iter-182: Cancel + Delete buttons — visible to admins only
            (require_admin gates the endpoints too). Hidden once the task is
            already finalised (audited) or cancelled.
            iter-218 (Feb 2026) — The "حذف نهائي" button is now split from
            "إلغاء الطلب": it stays visible for the primary admin
            (admin@asb606.com) EVEN AFTER the task is closed & finalised,
            so the account owner can always purge test/mistake records.
            Cancel still hides once the task is finalised/cancelled. */}
        {((!isFinalised && task.status !== 'cancelled') || isSuperAdmin) && (
          <div className="flex flex-wrap gap-2 mb-5 justify-end">
            {!isFinalised && task.status !== 'cancelled' && (
              <button
                onClick={() => setCancelOpen(true)}
                data-testid="task-cancel-btn"
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium bg-amber-500/10 border border-amber-500/40 text-amber-200 hover:bg-amber-500/20"
              >
                <XCircle className="w-3.5 h-3.5" />
                {t('إلغاء الطلب', 'Cancel request')}
              </button>
            )}
            {isAdminRole && (!isFinalised || isSuperAdmin) && (
              <button
                onClick={deleteTaskHard}
                data-testid="task-hard-delete-btn"
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium bg-red-500/10 border border-red-500/40 text-red-200 hover:bg-red-500/20"
              >
                <Trash2 className="w-3.5 h-3.5" />
                {t('حذف نهائي', 'Delete permanently')}
              </button>
            )}
          </div>
        )}

        {/* Cancel modal */}
        {cancelOpen && (
          <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => (!cancelling && setCancelOpen(false))}>
            <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl max-w-md w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <XCircle className="w-5 h-5 text-amber-400" />
                {t('إلغاء الطلب', 'Cancel request')}
              </h3>
              <p className="text-xs text-[#808080]">
                {t('اكتب سبب الإلغاء — سيظهر عند العميل / التاجر مع رسالة "تم إلغاء الطلب".',
                   'Type the reason — the customer / merchant will see it alongside a "cancelled" flag.')}
              </p>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={3}
                className="w-full bg-[#050505] border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                placeholder={t('مثال: العميل ألغى بنفسه / خطأ في الإدراج / وثائق ناقصة', 'e.g. customer cancelled themselves / entry error / missing docs')}
                data-testid="task-cancel-reason"
              />
              <div className="flex justify-end gap-2 pt-2">
                <button
                  onClick={() => setCancelOpen(false)}
                  disabled={cancelling}
                  className="px-3 py-1.5 rounded-lg text-xs border border-[#2a2a4e] text-white hover:bg-white/5"
                >
                  {t('تراجع', 'Back')}
                </button>
                <button
                  onClick={submitCancel}
                  disabled={cancelling || !cancelReason.trim()}
                  data-testid="task-cancel-confirm"
                  className="px-3 py-1.5 rounded-lg text-xs bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-50"
                >
                  {cancelling
                    ? t('جاري الإلغاء…', 'Cancelling…')
                    : t('تأكيد الإلغاء', 'Confirm cancel')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Feb 2026 (v2) — Fee Model banner: tells the executing staff whether
            the customer has already paid the government fees (full package)
            or if they still owe them. */}
        {task.fee_model && (
          <FeeModelBanner task={task} t={t} isAr={isAr} />
        )}

        {/* Switch Button — 3-way tab: الطلب / الإجراءات / حركة الطلب */}
        <div className="flex items-center gap-2 mb-5 bg-[#050505] border border-white/10 rounded-xl p-1">
          <button
            onClick={() => setTab('request')}
            data-testid="task-detail-tab-request"
            className={`flex-1 h-11 rounded-lg text-xs md:text-sm font-bold flex items-center justify-center gap-2 transition ${
              tab === 'request' ? 'text-black' : 'text-white/60 hover:text-white'
            }`}
            style={tab === 'request' ? { backgroundColor: accent } : {}}
          >
            <ScrollText className="w-4 h-4" />
            {t('الطلب', 'Request')}
          </button>
          <button
            onClick={() => setTab('actions')}
            data-testid="task-detail-tab-actions"
            className={`flex-1 h-11 rounded-lg text-xs md:text-sm font-bold flex items-center justify-center gap-2 transition ${
              tab === 'actions' ? 'text-black' : 'text-white/60 hover:text-white'
            }`}
            style={tab === 'actions' ? { backgroundColor: accent } : {}}
          >
            <ClipboardList className="w-4 h-4" />
            {t('الإجراءات', 'Actions')}
            {(task.actions || []).length > 0 && (
              <span className="text-[10px] px-1.5 rounded-full bg-white/20 text-white">
                {task.actions.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setTab('movement')}
            data-testid="task-detail-tab-movement"
            className={`flex-1 h-11 rounded-lg text-xs md:text-sm font-bold flex items-center justify-center gap-2 transition ${
              tab === 'movement' ? 'text-black' : 'text-white/60 hover:text-white'
            }`}
            style={tab === 'movement' ? { backgroundColor: accent } : {}}
          >
            <GitBranch className="w-4 h-4" />
            {t('حركة الطلب', 'Movement')}
          </button>
        </div>

        {tab === 'request' ? (
          <RequestTab task={task} accent={accent} stages={stages} onToggle={toggleStage} onPatchStageDate={patchStageDate} isFinalised={isFinalised} t={t} isAr={isAr} />
        ) : tab === 'actions' ? (
          <ActionsTab
            task={task}
            accent={accent}
            staff={staff}
            onReload={load}
            isFinalised={isFinalised}
            isReadyForAudit={isReadyForAudit}
            onCloseAfterAudit={closeAfterAudit}
            t={t}
            isAr={isAr}
          />
        ) : (
          <MovementTab task={task} accent={accent} t={t} isAr={isAr} />
        )}
      </main>
    </div>
  );
}


// ── Request tab ─────────────────────────────────────────────────

// Feb 2026 (iter-184, iter-186) — Prominent 3-step status stepper.
// Simplified in iter-186 per user spec: the top-of-page indicator collapses
// the 7-stage pipeline into 3 canonical states so the admin can see the
// request's macro-status at a glance:
//   • فعال (Active)                       ← as soon as 'approved' is done
//   • قيد التنفيذ (In progress)           ← 'in_progress' is done
//   • تم الانتهاء وإغلاق الطلب (Closed)   ← 'closed' is done
// Cancelled tasks show a red single-pill instead of the stepper.
// iter-202 (Feb 2026) — StageDateEditor
// Renders the stage's completion timestamp with an inline "edit" affordance.
// Clicking the pencil switches it into a datetime-local input; the admin can
// backdate the timestamp (or forward-date it, no restriction — trust the
// admin) and hit Save. On success the parent's task state is refreshed via
// `onSave` (which calls the /stage-date endpoint) and the SR mirror on the
// merchant platform picks it up automatically.
function StageDateEditor({ stageKey, currentAt, by, isAr, canEdit, onSave, t }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  const openEdit = () => {
    // Convert ISO → "YYYY-MM-DDTHH:mm" for <input type="datetime-local">
    try {
      const d = new Date(currentAt);
      const pad = (n) => String(n).padStart(2, '0');
      const local = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
      setValue(local);
    } catch {
      setValue('');
    }
    setEditing(true);
  };

  const submit = async () => {
    if (!value) {
      toast.error(t('اختر تاريخاً/وقتاً صحيحاً', 'Pick a valid date/time'));
      return;
    }
    setSaving(true);
    // datetime-local yields a local ISO like "2026-01-05T14:30" — expand to full ISO.
    const iso = new Date(value).toISOString();
    const ok = await onSave(stageKey, iso);
    setSaving(false);
    if (ok) setEditing(false);
  };

  if (editing) {
    return (
      <div className="mt-1.5 flex items-center gap-1.5 flex-wrap" data-testid={`stage-date-editor-${stageKey}`}>
        <input
          type="datetime-local"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="px-2 py-1 bg-black/50 border border-amber-500/40 rounded-md text-white text-[11px] focus:outline-none focus:border-amber-400"
          data-testid={`stage-date-input-${stageKey}`}
          disabled={saving}
        />
        <button
          onClick={submit}
          disabled={saving}
          className="p-1 rounded-md bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 disabled:opacity-50"
          title={t('حفظ', 'Save')}
          data-testid={`stage-date-save-${stageKey}`}
        >
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
        </button>
        <button
          onClick={() => setEditing(false)}
          disabled={saving}
          className="p-1 rounded-md bg-white/5 text-white/60 hover:bg-white/10"
          title={t('إلغاء', 'Cancel')}
          data-testid={`stage-date-cancel-${stageKey}`}
        >
          <X className="w-3 h-3" />
        </button>
      </div>
    );
  }

  return (
    <div className="text-[11px] text-emerald-400/70 mt-0.5 flex items-center gap-1.5 flex-wrap" data-testid={`stage-date-view-${stageKey}`}>
      <span className="font-mono">
        {new Date(currentAt).toLocaleString(isAr ? 'ar-EG' : 'en-GB')}
      </span>
      {by ? <span className="text-emerald-400/50">• {by}</span> : null}
      {canEdit && (
        <button
          onClick={openEdit}
          className="p-0.5 rounded text-white/40 hover:text-amber-300 hover:bg-amber-500/10 transition-colors"
          title={t('تعديل التاريخ', 'Edit date')}
          data-testid={`stage-date-edit-btn-${stageKey}`}
        >
          <Pencil className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}


function StatusHero({ task, t, isAr }) {
  const stages = task.request_stages || {};
  const done   = (k) => stages[k]?.done === true || stages[k]?.locked === true;

  // Cancelled? Bail out with a single red banner (no stepper).
  if (task.status === 'cancelled') {
    return (
      <div
        className="mb-5 rounded-2xl border p-4 flex items-center gap-4"
        style={{ borderColor: '#dc262655', backgroundColor: 'rgba(220,38,38,0.12)' }}
        data-testid="task-status-hero"
      >
        <div className="w-11 h-11 rounded-full bg-red-500/30 flex items-center justify-center">
          <XCircle className="w-5 h-5 text-red-400" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-white/50 font-semibold">
            {t('حالة الطلب', 'Request status')}
          </div>
          <div className="text-lg font-bold text-red-400">{t('ملغى', 'Cancelled')}</div>
        </div>
      </div>
    );
  }

  const isActive     = done('approved');    // becomes true right after creation (locked)
  const isInProgress = done('in_progress');
  const isClosed     = done('closed');

  // Highlight the LAST completed step so admins see where the task sits.
  let currentStep = -1;
  if (isActive)      currentStep = 0;
  if (isInProgress)  currentStep = 1;
  if (isClosed)      currentStep = 2;

  const STEPS = [
    { label: t('فعال',                          'Active'),
      Icon:  Activity, done:  isActive,     color: '#0ea5e9' },
    { label: t('قيد التنفيذ',                    'In progress'),
      Icon:  Send,     done:  isInProgress, color: '#d4af37' },
    { label: t('تم الانتهاء وإغلاق الطلب',       'Completed & Closed'),
      Icon:  Lock,     done:  isClosed,     color: '#10b981' },
  ];

  return (
    <div className="mb-5 bg-white/[0.02] border border-white/10 rounded-2xl p-4"
         data-testid="task-status-hero">
      <div className="text-[10px] uppercase tracking-wider text-white/40 font-semibold mb-3">
        {t('حالة الطلب', 'Request status')}
      </div>
      <div className="flex items-center gap-2 md:gap-3 flex-wrap md:flex-nowrap">
        {STEPS.map((s, idx) => {
          const StepIcon = s.Icon;
          const isCurrent = idx === currentStep;
          return (
            <Fragment key={idx}>
              <div
                className="flex-1 min-w-0 flex items-center gap-2 md:gap-3 p-3 rounded-xl border transition"
                style={s.done
                  ? { borderColor: `${s.color}55`, backgroundColor: `${s.color}18`,
                      boxShadow: isCurrent ? `0 0 0 2px ${s.color}66 inset` : undefined }
                  : { borderColor: 'rgba(255,255,255,0.08)', backgroundColor: 'rgba(255,255,255,0.02)' }}
                data-testid={`status-step-${idx}`}
              >
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                  style={s.done
                    ? { backgroundColor: `${s.color}30`, color: s.color }
                    : { backgroundColor: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.35)' }}
                >
                  <StepIcon className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <div className={`text-[10px] uppercase tracking-wider font-semibold ${s.done ? 'text-white/60' : 'text-white/30'}`}>
                    {t(`المرحلة ${idx + 1}`, `Step ${idx + 1}`)}
                  </div>
                  <div className="text-xs md:text-sm font-bold truncate"
                       style={s.done ? { color: s.color } : { color: 'rgba(255,255,255,0.4)' }}>
                    {s.label}
                  </div>
                </div>
                {s.done && <CheckCircle className="w-4 h-4 ms-auto flex-shrink-0" style={{ color: s.color }} />}
              </div>

              {idx < STEPS.length - 1 && (
                <div className="hidden md:block flex-shrink-0 h-0.5 w-4 md:w-6"
                     style={{ backgroundColor: STEPS[idx + 1].done ? STEPS[idx + 1].color : 'rgba(255,255,255,0.08)' }} />
              )}
            </Fragment>
          );
        })}
      </div>

      {task.review_status === 'ready_for_review' && (
        <div className="mt-3 text-xs px-3 py-2 rounded-lg bg-[#d4af37]/10 border border-[#d4af37]/30 text-[#d4af37] inline-flex items-center gap-1.5">
          <ClipboardCheck className="w-3.5 h-3.5" />
          {t('في انتظار التدقيق النهائي', 'Waiting for final audit')}
        </div>
      )}
      {task.review_status === 'closed' && (
        <div className="mt-3 text-xs px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 inline-flex items-center gap-1.5">
          <CheckCircle className="w-3.5 h-3.5" />
          {t('مغلق ومدقق نهائياً', 'Finalised & audited')}
        </div>
      )}
    </div>
  );
}


function RequestTab({ task, accent, stages, onToggle, onPatchStageDate, isFinalised, t, isAr }) {
  return (
    <div className="space-y-4">
      {/* AI conversation transcript — only shown for AI-generated tasks */}
      {task.source === 'ai_generated' && (task.ai_conversation || []).length > 0 && (
        <AIConversationCard conversation={task.ai_conversation}
                            agentId={task.ai_agent_id}
                            t={t} isAr={isAr} />
      )}

      {/* Request details */}
      <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-5">
        <h2 className="text-white font-bold text-sm mb-3">{t('تفاصيل الطلب', 'Request details')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <InfoRow label={t('الأولوية', 'Priority')} value={task.priority || '—'} />
          <InfoRow label={t('مصدر الطلب', 'Source')} value={task.request_source || '—'} />
          <InfoRow label={t('تاريخ التقديم', 'Submitted')} value={task.request_date || task.created_at?.slice(0, 10) || '—'} />
          <InfoRow label={t('المُنشئ', 'Created by')} value={task.created_by || '—'} />
        </div>
        {task.description_ar && (
          <div className="mt-3">
            <div className="text-xs text-white/50 mb-1">{t('وصف الطلب', 'Description')}</div>
            <div className="text-sm text-white/80 bg-white/[0.03] border border-white/10 rounded-lg p-3 whitespace-pre-wrap">
              {task.description_ar}
            </div>
          </div>
        )}
        {task.request_note && (
          <div className="mt-3">
            <div className="text-xs text-white/50 mb-1">{t('ملاحظات داخلية', 'Internal note')}</div>
            <div className="text-sm text-white/70 bg-amber-500/5 border border-amber-500/20 rounded-lg p-3 whitespace-pre-wrap">
              {task.request_note}
            </div>
          </div>
        )}
      </div>

      {/* Requestor Card — iter-188 (Feb 2026). AGENT tasks only. Shows the
          identity of the person who submitted the request on behalf of the
          target agent (name / email / phone captured at creation). Card is
          hidden when none of the three fields are populated. */}
      {task.target_type === 'agent' && (task.requestor_name || task.requestor_email || task.requestor_phone) && (
        <RequestorCard task={task} t={t} isAr={isAr} />
      )}

      {/* Attachments Card — Feb 2026 (iter-187) */}
      <AttachmentsCard task={task} t={t} isAr={isAr} />

      {/* Service Fees Card — Feb 2026 (iter-183): between Request details and
          Request stages. Admin sets service_fee + fee_model here. Gov fees
          are auto-summed from actions with has_fee=true. */}
      <ServiceFeesCard task={task} isFinalised={isFinalised} t={t} isAr={isAr} />

      {/* Fees Settlement Card — iter-189. Locked once the `fees_collected`
          stage is done (iter-190) so admin can't edit payments after
          declaring fees fully collected. */}
      <FeesSettlementCard
        task={task}
        isFinalised={isFinalised}
        isLocked={stages.fees_collected?.done === true}
        onReload={() => window.location.reload()}
        t={t}
        isAr={isAr}
      />

      {/* Commission Box — iter-190. Moved OUT of the stages loop to be its
          own standalone card. Admin enters commission % here BEFORE toggling
          the `commission_collected` stage (backend refuses the toggle when
          commission_percent + commission_amount are missing). Locked once
          `commission_collected` stage is done. */}
      {task.target_type === 'agent' && (
        <CommissionBox
          task={task}
          isFinalised={isFinalised}
          isLocked={stages.commission_collected?.done === true}
          t={t}
          isAr={isAr}
        />
      )}

      {/* Timeline — Agent tasks only */}
      {task.target_type === 'agent' && (
        <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <h2 className="text-white font-bold text-sm">{t('مراحل الطلب', 'Request stages')}</h2>
            <span className="text-xs text-white/40">
              {t('(تسلسلية — أكمل السابقة أولاً)', '(sequential — complete the previous first)')}
            </span>
          </div>
          <ol className="space-y-1">
            {STAGE_CONFIG.map((s, idx) => {
              const info = stages[s.key] || {};
              const done = info.done === true;
              const locked = info.locked === true;
              const Icon = s.Icon;
              // Sequential enforcement: a stage becomes "toggleable" only when
              // the previous stage is done (or is a locked-done stage).
              let prevDone = idx === 0;
              if (idx > 0) {
                const prev = stages[STAGE_CONFIG[idx - 1].key] || {};
                prevDone = prev.done === true || prev.locked === true;
              }
              const canToggle = !locked && !isFinalised && prevDone;
              return (
                <div key={s.key} data-testid={`stage-container-${s.key}`}>
                  <li
                    className={`flex items-center gap-3 p-3 rounded-lg border transition ${
                      done ? 'bg-emerald-500/5 border-emerald-500/30'
                           : canToggle ? 'bg-white/[0.02] border-white/10'
                                       : 'bg-white/[0.01] border-white/5 opacity-60'
                    }`}
                    data-testid={`stage-row-${s.key}`}
                  >
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center ${
                      done ? 'bg-emerald-500/20 text-emerald-300'
                           : canToggle ? 'bg-white/10 text-white/60'
                                       : 'bg-white/5 text-white/25'
                    }`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-white flex items-center gap-2">
                        <span className="text-white/40 text-xs font-mono">{idx + 1}.</span>
                        {isAr ? s.ar : s.en}
                        {locked && <Lock className="w-3 h-3 text-white/30" />}
                      </div>
                      {done && info.at && (
                        <StageDateEditor
                          stageKey={s.key}
                          currentAt={info.at}
                          by={info.by}
                          isAr={isAr}
                          // iter-216 — locked stages (auto-approved
                          // pending_review + approved) can still be
                          // backdated; the lock only prevents un-ticking.
                          canEdit={!isFinalised}
                          onSave={onPatchStageDate}
                          t={t}
                        />
                      )}
                      {!done && !locked && !canToggle && (
                        <div className="text-[11px] text-white/30 mt-0.5">
                          {t('أكمل المرحلة السابقة أولاً', 'Complete the previous stage first')}
                        </div>
                      )}
                    </div>
                    {!locked && (
                      <label className={`inline-flex items-center gap-2 ${canToggle ? 'cursor-pointer' : 'opacity-40 cursor-not-allowed'}`}
                             data-testid={`stage-toggle-${s.key}`}>
                        <input type="checkbox" checked={done}
                               disabled={!canToggle}
                               onChange={(e) => onToggle(s.key, e.target.checked)}
                               className="w-5 h-5 accent-[#d4af37] disabled:cursor-not-allowed" />
                        <span className="text-xs text-white/60">
                          {done ? t('تم', 'Done') : t('غير مكتمل', 'Pending')}
                        </span>
                      </label>
                    )}
                  </li>
                </div>
              );
            })}
          </ol>
        </div>
      )}
    </div>
  );
}


// ── Actions tab ─────────────────────────────────────────────────
function ActionsTab({ task, accent, staff, onReload, isFinalised, isReadyForAudit, onCloseAfterAudit, t, isAr }) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [description, setDescription] = useState('');
  // Feb 2026 (iter-183) — Two-question flow per new procedure:
  //   Q1: Does this procedure have a monetary amount?  → hasFee
  //        (if yes: amount + receipt with AI OCR extraction)
  //   Q2: Are there attachments?                       → hasAttachments
  //        (if yes: attachments picker, up to MAX_ATT files)
  const [hasFee, setHasFee] = useState(false);
  const [amount, setAmount] = useState('');
  // Feb 2026 (iter-185) — Multiple receipts per procedure (max 5).
  // Each: { base64, mime, name, amount, verified, verifying }
  // `verified=true` means the AI confirmed it's an actual receipt.
  const [receipts, setReceipts] = useState([]);
  const [hasAttachments, setHasAttachments] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [transferId, setTransferId] = useState('');
  const [saving, setSaving] = useState(false);

  const MAX_RECEIPTS = 5;

  // Gov fees total = sum of has_fee actions (per user spec — الرسوم الحكومية تُجمع من الإجراءات).
  const govFeesTotal = (task.actions || [])
    .filter(a => a.has_fee)
    .reduce((s, a) => s + (Number(a.amount) || 0), 0);
  const grandTotal   = task.total_amount ?? (task.actions || []).reduce((s, a) => s + (Number(a.amount) || 0), 0);

  const resetAddForm = () => {
    setDescription(''); setHasFee(false); setAmount('');
    setReceipts([]);
    setHasAttachments(false); setAttachments([]);
    setTransferId('');
  };

  // Feb 2026 (iter-185) — Runs the AI receipt extractor + verification.
  // Rejects files that aren't actually receipts (returns null in that case).
  // On success returns the parsed data { amount, reference, date, ... }.
  // iter-217c-v16 — On HTTP error (500/424/…) axios only gives us a generic
  // "Request failed with status code 500" — surface the backend's `detail`
  // field so the user sees the real reason (bad model, key rejected, quota …).
  const extractAndVerifyReceipt = async (base64, mime) => {
    let res;
    try {
      res = await axios.post(
        `${API_URL}/api/admin/assigned-tasks/receipts/extract`,
        { file_base64: base64, mime },
        { headers: getAuthHeaders(), timeout: 60_000 },
      );
    } catch (err) {
      const backendDetail = err?.response?.data?.detail;
      throw new Error(
        backendDetail
        || err?.message
        || t('فشل قراءة الإيصال', 'Receipt read failed'),
      );
    }
    const data = res.data?.data || {};
    if (!data.is_receipt) {
      const reason = data.reject_reason || t('الملف لا يحتوي على بيانات إيصال', 'File does not contain receipt data');
      throw new Error(t(`هذا الملف ليس إيصالاً — ${reason}`, `This is not a receipt — ${reason}`));
    }
    return data;
  };

  const onPick = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    if (attachments.length + files.length > MAX_ATT) {
      toast.error(t(`الحد ${MAX_ATT} مرفقات`, `Max ${MAX_ATT} attachments`));
      return;
    }
    const acceptedTypes = /^(application\/pdf|image\/(png|jpe?g|webp|heic|heif))$/i;
    const arr = [];
    for (const f of files) {
      if (!acceptedTypes.test(f.type)) {
        toast.error(t(`${f.name}: نوع غير مدعوم`, `${f.name}: unsupported type`));
        continue;
      }
      if (f.size > MAX_ATT_MB * 1024 * 1024) {
        toast.error(t(`${f.name}: أكبر من ${MAX_ATT_MB}MB`, `${f.name}: exceeds ${MAX_ATT_MB}MB`));
        continue;
      }
      const dataUrl = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.onerror = rej;
        r.readAsDataURL(f);
      });
      arr.push({ name: f.name, label: '', mime_type: f.type, size: f.size, data_url: dataUrl });
    }
    setAttachments(prev => [...prev, ...arr]);
  };

  const submitAction = async () => {
    if (!description.trim()) {
      toast.error(t('اكتب وصف الإجراء', 'Please enter an action description'));
      return;
    }
    if (hasFee && (Number(amount) <= 0 || !amount)) {
      toast.error(t('أدخل مبلغ الرسم (أكبر من صفر)', 'Enter the fee amount (greater than zero)'));
      return;
    }
    if (hasFee && receipts.length === 0) {
      toast.error(t('ارفق إيصال واحد على الأقل', 'Attach at least one receipt'));
      return;
    }
    if (hasFee && receipts.some(r => !r.verified)) {
      toast.error(t('انتظر التحقق من كل الإيصالات', 'Wait until all receipts are verified'));
      return;
    }
    if (hasAttachments && attachments.some(a => !a.label.trim())) {
      toast.error(t('اكتب اسم لكل مرفق', 'Enter a name for every attachment'));
      return;
    }
    setSaving(true);
    try {
      const staffPicked = transferId ? staff.find(s => s.id === transferId) : null;
      const body = {
        description: description.trim(),
        has_fee:     hasFee,
        amount:      hasFee ? (Number(amount) || 0) : 0,
        // Feb 2026 (iter-185) — multi-receipts payload.
        receipts:    hasFee ? receipts.map(r => ({
          file: r.base64, mime: r.mime, name: r.name,
          amount: r.amount ?? 0, reference: r.reference ?? '', date: r.date ?? '',
        })) : [],
        attachments: hasAttachments ? attachments : [],
        ...(staffPicked ? {
          transfer_to_id:    staffPicked.id,
          transfer_to_name:  staffPicked.name || staffPicked.email,
          transfer_to_email: staffPicked.email,
        } : {}),
      };
      await axios.post(
        `${API_URL}/api/admin/assigned-tasks/${task.id}/actions`,
        body,
        { headers: getAuthHeaders() },
      );
      toast.success(t('تم إضافة الإجراء', 'Action added'));
      resetAddForm();
      setShowAddForm(false);
      onReload();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشل الإضافة', 'Add failed'));
    } finally {
      setSaving(false);
    }
  };

  const removeAction = async (actionId) => {
    if (!window.confirm(t('حذف هذا الإجراء؟', 'Delete this action?'))) return;
    try {
      await axios.delete(
        `${API_URL}/api/admin/assigned-tasks/${task.id}/actions/${actionId}`,
        { headers: getAuthHeaders() },
      );
      onReload();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشل الحذف', 'Delete failed'));
    }
  };

  return (
    <div className="space-y-4">
      {/* Info alert — internal */}
      <div className="bg-amber-500/5 border border-amber-500/30 rounded-lg p-3 text-xs text-amber-300/90 flex items-start gap-2">
        <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
        {t('الإجراءات داخلية بحتة — لا تظهر للوكيل في منصته. تُستخدم لتتبع الخطوات الفعلية داخل الشركة والمبالغ المرتبطة بها.',
           'Actions are strictly internal — invisible to the agent on their platform. Used to log the internal steps and any related amounts.')}
      </div>

      {/* Total row — Gov fees (from has_fee actions) + Grand total */}
      {(grandTotal > 0 || govFeesTotal > 0) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="bg-cyan-500/5 border border-cyan-500/25 rounded-2xl p-4 flex items-center justify-between">
            <span className="text-cyan-300 text-sm">{t('الرسوم الحكومية (مجموع الرسوم في الإجراءات)', 'Government fees (sum of fee actions)')}</span>
            <span className="text-xl font-bold text-cyan-200" data-testid="actions-gov-total">
              <DirhamPrice value={govFeesTotal} decimals={2} size="sm" />
            </span>
          </div>
          <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-4 flex items-center justify-between">
            <span className="text-white/60 text-sm">{t('المجموع الكلي للمبالغ', 'Grand total')}</span>
            <span className="text-xl font-bold text-[#d4af37]" data-testid="actions-total">
              <DirhamPrice value={grandTotal} decimals={2} size="sm" />
            </span>
          </div>
        </div>
      )}

      {/* Actions list */}
      <div className="space-y-2">
        {(task.actions || []).length === 0 ? (
          <div className="p-8 text-center text-white/40 text-sm bg-white/[0.02] border border-dashed border-white/10 rounded-2xl">
            {t('لا توجد إجراءات بعد. أضف أول خطوة.', 'No actions yet. Add the first step.')}
          </div>
        ) : (
          (task.actions || []).map((a, idx) => (
            <div key={a.id || idx}
                 className="bg-white/[0.02] border border-white/10 rounded-xl p-4"
                 data-testid={`action-row-${idx}`}>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center flex-shrink-0 text-xs text-white/60 font-mono">
                  {idx + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white whitespace-pre-wrap">{a.description}</div>
                  <div className="text-[11px] text-white/40 mt-1 flex items-center gap-2 flex-wrap">
                    <span>{new Date(a.created_at).toLocaleString(isAr ? 'ar-EG' : 'en-GB')}</span>
                    {a.created_by_name && <span>• {a.created_by_name}</span>}
                    {a.has_fee && (
                      <span className="inline-flex items-center gap-1 text-cyan-300 bg-cyan-500/10 border border-cyan-500/25 rounded-full px-2 py-0.5">
                        <Coins className="w-3 h-3" />
                        {t('رسم حكومي', 'Gov fee')}
                      </span>
                    )}
                    {a.transfer_to_name && (
                      <span className="inline-flex items-center gap-1 text-purple-300">
                        <ArrowRightLeft className="w-3 h-3" />
                        {t('تحويل إلى', 'transferred to')} {a.transfer_to_name}
                      </span>
                    )}
                  </div>
                  {a.has_fee && (Array.isArray(a.receipts) ? a.receipts.length > 0 : !!a.receipt_file) && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {(Array.isArray(a.receipts) && a.receipts.length
                        ? a.receipts
                        : [{ file: a.receipt_file, mime: a.receipt_mime, name: a.receipt_name || t('إيصال الرسم', 'Fee receipt') }]
                      ).map((r, i) => (
                        <a key={i}
                           href={`data:${r.mime || 'application/octet-stream'};base64,${r.file}`}
                           target="_blank" rel="noopener noreferrer"
                           download={r.name || `receipt-${i + 1}`}
                           className="inline-flex items-center gap-2 bg-cyan-500/5 border border-cyan-500/25 rounded-md px-2.5 py-1.5 text-[11px] text-cyan-200 hover:bg-cyan-500/10"
                           data-testid={`action-receipt-${idx}-${i}`}>
                          <FileText className="w-3.5 h-3.5" />
                          <span className="truncate max-w-[180px]">{r.name || `${t('إيصال', 'Receipt')} ${i + 1}`}</span>
                          {r.amount != null && (
                            <span className="text-cyan-300/80 font-mono">
                              <DirhamPrice value={r.amount} decimals={2} size="sm" />
                            </span>
                          )}
                        </a>
                      ))}
                    </div>
                  )}
                  {(a.attachments || []).length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
                      {a.attachments.map((att, i) => (
                        <button key={i} type="button"
                           onClick={(e) => { e.preventDefault(); e.stopPropagation(); openAttachment(att); }}
                           className="flex items-center gap-2 bg-black/40 border border-white/10 rounded-md p-2 hover:bg-black/60 text-start"
                           data-testid={`action-att-open-${i}`}>
                          {(att.mime_type || '').startsWith('image/')
                            ? <ImageIcon className="w-3.5 h-3.5 text-blue-300" />
                            : <FileText className="w-3.5 h-3.5 text-red-300" />}
                          <span className="text-[11px] text-white/70 truncate">{att.label || att.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1">
                  {Number(a.amount) > 0 && (
                    <div className="text-sm font-bold text-emerald-400 font-mono">
                      <DirhamPrice value={a.amount} decimals={2} size="sm" />
                    </div>
                  )}
                  {!isFinalised && (
                    <button onClick={() => removeAction(a.id)}
                            className="p-1.5 rounded hover:bg-red-500/10 text-red-400/70 hover:text-red-400"
                            data-testid={`action-delete-${idx}`}>
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add action form — hidden when finalised */}
      {!isFinalised && (
        showAddForm ? (
          <div className="bg-white/[0.03] border border-[#d4af37]/30 rounded-2xl p-5 space-y-4" data-testid="action-add-form">
            <div className="flex items-center justify-between">
              <h3 className="text-white font-bold text-sm">{t('إضافة إجراء جديد', 'Add new action')}</h3>
              <button onClick={() => { setShowAddForm(false); resetAddForm(); }}
                      className="p-1.5 rounded hover:bg-white/5 text-white/60">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div>
              <label className="text-xs text-white/50 mb-1 block">{t('وصف الإجراء *', 'Action description *')}</label>
              <textarea value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        rows={3}
                        placeholder={t('مثال: تم إنجاز فتح ملف الجوازات ورفع المستند', 'e.g. Passport file opened and document uploaded')}
                        className="w-full bg-[#050505] border border-white/10 text-white rounded-md p-2.5 text-sm resize-y"
                        dir={isAr ? 'rtl' : 'ltr'}
                        data-testid="action-desc" />
            </div>

            {/* Q1 — has fee? */}
            <div className="bg-[#050505] border border-white/10 rounded-lg p-3 space-y-3">
              <label className="flex items-center gap-2 cursor-pointer text-sm text-white" data-testid="action-hasfee-toggle">
                <input type="checkbox" checked={hasFee}
                       onChange={(e) => {
                         setHasFee(e.target.checked);
                         if (!e.target.checked) { setAmount(''); setReceipts([]); }
                       }}
                       className="w-4 h-4 accent-emerald-500" />
                <span>{t('هل يوجد لهذا الإجراء مبلغ مالي؟', 'Does this procedure have a monetary amount?')}</span>
              </label>

              {hasFee && (
                <div className="ps-6 space-y-3">
                  {/* Amount input */}
                  <div>
                    <label className="text-[10px] text-white/50 mb-1 block">
                      {t('المبلغ الإجمالي', 'Total amount')} <span className="text-red-400">*</span>
                      <span className="text-white/30 ms-2">
                        {t('(يُحسب تلقائياً من الإيصالات)', '(auto-summed from receipts)')}
                      </span>
                    </label>
                    <Input type="number" step="0.01" min="0" value={amount}
                           onChange={(e) => setAmount(e.target.value)}
                           placeholder="0.00" className="bg-black border-white/10 text-white h-9 max-w-[200px]"
                           data-testid="action-amount" />
                  </div>

                  {/* Receipts list */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-[10px] text-white/50">
                        {t('الإيصالات', 'Receipts')} <span className="text-red-400">*</span>
                        <span className="text-white/30 ms-2">({receipts.length}/{MAX_RECEIPTS})</span>
                      </label>
                    </div>

                    {/* Uploaded receipts */}
                    {receipts.length > 0 && (
                      <div className="space-y-1.5 mb-2">
                        {receipts.map((r, i) => (
                          <div key={i}
                               className={`flex items-center gap-2 bg-black border rounded-md px-3 py-1.5 ${
                                 r.verifying ? 'border-cyan-500/40' :
                                 r.verified  ? 'border-emerald-500/40' :
                                                'border-red-500/40'
                               }`}
                               data-testid={`action-receipt-item-${i}`}>
                            {r.verifying ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin text-cyan-300 flex-shrink-0" />
                            ) : r.verified ? (
                              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
                            ) : (
                              <XCircle className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="text-xs text-white truncate">{r.name}</div>
                              {r.verifying && (
                                <div className="text-[10px] text-cyan-300">{t('جاري التحقق بالذكاء الاصطناعي…', 'Verifying with AI…')}</div>
                              )}
                              {r.verified && r.amount != null && (
                                <div className="text-[10px] text-emerald-400">
                                  {t('إيصال صحيح', 'Valid receipt')} · {t('المبلغ:', 'Amount:')} {r.amount} {r.reference ? `· ${r.reference}` : ''}
                                </div>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => setReceipts((prev) => {
                                const next = prev.filter((_, idx) => idx !== i);
                                // Recompute total amount from remaining receipts.
                                const sum = next.filter(x => x.verified).reduce((s, x) => s + (Number(x.amount) || 0), 0);
                                setAmount(sum > 0 ? String(sum) : '');
                                return next;
                              })}
                              className="p-1 rounded hover:bg-red-500/10 text-red-400"
                              data-testid={`action-receipt-remove-${i}`}
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Add-more receipt button */}
                    {receipts.length < MAX_RECEIPTS && (
                      <label
                        className="inline-flex items-center gap-2 cursor-pointer px-3 py-1.5 rounded-md bg-black border border-dashed border-white/20 hover:border-emerald-500/50 text-xs text-white/80"
                        data-testid="action-add-receipt-btn"
                      >
                        <Paperclip className="w-3.5 h-3.5" />
                        {receipts.length === 0
                          ? t('اختر إيصال', 'Choose receipt')
                          : t('أضف إيصال آخر', 'Add another receipt')}
                        <input
                          type="file"
                          accept="image/*,application/pdf"
                          multiple={false}
                          className="hidden"
                          onChange={async (e) => {
                            const f = e.target.files?.[0];
                            e.target.value = '';
                            if (!f) return;
                            if (f.size > MAX_ATT_MB * 1024 * 1024) {
                              toast.error(t(`الحد ${MAX_ATT_MB} ميغا`, `Max ${MAX_ATT_MB}MB`));
                              return;
                            }
                            const reader = new FileReader();
                            reader.onload = async () => {
                              const [prefix, base64] = String(reader.result).split(',');
                              const m = /^data:([^;]+);base64$/.exec(prefix);
                              const mime = m ? m[1] : f.type;
                              // Add placeholder (verifying:true) so the UI shows spinner + name.
                              let idx = -1;
                              setReceipts((prev) => {
                                const next = [...prev, { base64, mime, name: f.name, verifying: true, verified: false }];
                                idx = next.length - 1;
                                return next;
                              });
                              try {
                                const data = await extractAndVerifyReceipt(base64, mime);
                                setReceipts((prev) => {
                                  const next = prev.map((it, i) => (i === idx ? {
                                    ...it,
                                    verifying: false,
                                    verified:  true,
                                    amount:    Number(data.amount || 0),
                                    reference: data.reference || '',
                                    date:      data.date || '',
                                  } : it));
                                  // Sum verified receipts → amount field.
                                  const sum = next.filter(x => x.verified).reduce((s, x) => s + (Number(x.amount) || 0), 0);
                                  setAmount(sum > 0 ? String(sum) : '');
                                  return next;
                                });
                                toast.success(t(`تم التحقق: ${data.amount || 0}`, `Verified: ${data.amount || 0}`));
                              } catch (err) {
                                // Rejected — remove the placeholder from the list.
                                setReceipts((prev) => prev.filter((_, i) => i !== idx));
                                toast.error(err.message || t('فشل قراءة الإيصال', 'Receipt read failed'));
                              }
                            };
                            reader.readAsDataURL(f);
                          }}
                        />
                      </label>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Q2 — has attachments? */}
            <div className="bg-[#050505] border border-white/10 rounded-lg p-3 space-y-3">
              <label className="flex items-center gap-2 cursor-pointer text-sm text-white" data-testid="action-hasattachments-toggle">
                <input type="checkbox" checked={hasAttachments}
                       onChange={(e) => {
                         setHasAttachments(e.target.checked);
                         if (!e.target.checked) setAttachments([]);
                       }}
                       className="w-4 h-4 accent-emerald-500" />
                <span>{t('هل توجد مرفقات لهذا الإجراء؟', 'Are there attachments for this procedure?')}</span>
              </label>

              {hasAttachments && (
                <div className="ps-6 space-y-2">
                  {attachments.length > 0 && (
                    <div className="space-y-2">
                      {attachments.map((a, i) => (
                        <div key={i} className="flex items-center gap-2 bg-black border border-white/10 rounded-lg p-2">
                          {a.mime_type?.startsWith('image/')
                            ? <ImageIcon className="w-4 h-4 text-blue-300" />
                            : <FileText className="w-4 h-4 text-red-300" />}
                          <Input value={a.label}
                                 onChange={(e) => setAttachments(prev => prev.map((x, k) => k === i ? { ...x, label: e.target.value } : x))}
                                 placeholder={t('اسم المرفق *', 'Attachment name *')}
                                 className={`flex-1 bg-transparent text-white h-8 text-sm ${
                                   a.label.trim() === '' ? 'border-amber-500/60' : 'border-white/10'
                                 }`}
                                 data-testid={`action-att-name-${i}`} />
                          <span className="text-[10px] text-white/40">{(a.size / 1024).toFixed(0)}KB</span>
                          <button onClick={() => setAttachments(prev => prev.filter((_, k) => k !== i))}
                                  className="p-1 rounded hover:bg-red-500/10 text-red-400">
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {attachments.length < MAX_ATT && (
                    <label htmlFor="action-att-input"
                           className="inline-flex items-center gap-2 cursor-pointer px-3 py-1.5 rounded-lg border border-dashed border-white/20 hover:border-white/40 text-xs text-white/70">
                      <Paperclip className="w-3.5 h-3.5" />
                      {t('إضافة مرفق', 'Add attachment')}
                      <span className="text-white/40">({attachments.length}/{MAX_ATT})</span>
                    </label>
                  )}
                  <input id="action-att-input" type="file" multiple hidden
                         accept="application/pdf,image/*"
                         onChange={(e) => { onPick(e.target.files); e.target.value = ''; }} />
                </div>
              )}
            </div>

            {/* Optional transfer */}
            <div>
              <label className="text-xs text-white/50 mb-1 block">
                {t('تحويل للموظف التالي (اختياري)', 'Transfer to next employee (optional)')}
              </label>
              <select value={transferId} onChange={(e) => setTransferId(e.target.value)}
                      dir={isAr ? 'rtl' : 'ltr'}
                      className="w-full bg-[#050505] border border-white/10 text-white rounded-md h-10 px-2 text-sm"
                      data-testid="action-transfer">
                <option value="">{t('— بدون تحويل —', '— No transfer —')}</option>
                {staff.map(s => (
                  <option key={s.id} value={s.id}>{s.name || s.email}</option>
                ))}
              </select>
            </div>

            <Button onClick={submitAction} disabled={saving}
                    className="w-full text-black font-bold h-11"
                    style={{ backgroundColor: accent }}
                    data-testid="action-submit">
              {saving ? <Loader2 className="w-4 h-4 animate-spin me-2" /> : <Plus className="w-4 h-4 me-2" />}
              {t('حفظ الإجراء', 'Save action')}
            </Button>
          </div>
        ) : (
          <button onClick={() => setShowAddForm(true)}
                  data-testid="action-add-open"
                  className="w-full py-3 rounded-2xl border-2 border-dashed border-white/15 hover:border-white/30 hover:bg-white/[0.02] text-white/60 hover:text-white flex items-center justify-center gap-2 transition">
            <Plus className="w-4 h-4" />
            {t('إضافة إجراء جديد', 'Add new action')}
          </button>
        )
      )}

      {/* Close after audit — visible only when review_status is ready_for_review (task in "المنجزة والجاهزة للتدقيق" bucket).
          The old "submit for review" button is gone — that transition now happens
          automatically when the "closed" stage is toggled in the Request tab. */}
      {task.target_type === 'agent' && isReadyForAudit && (
        <div className="mt-6 pt-5 border-t border-white/10">
          <Button onClick={onCloseAfterAudit}
                  className="w-full text-black font-bold h-12"
                  style={{ backgroundColor: '#10b981' }}
                  data-testid="close-audit-btn">
            <Lock className="w-4 h-4 me-2" />
            {t('الإغلاق النهائي بعد التدقيق', 'Finalise task after audit')}
          </Button>
          <div className="text-[11px] text-white/40 text-center mt-2">
            {t('بعد الإغلاق النهائي: تنتقل المهمة إلى "المهام المغلقة والمدققة" وتُقفل ضد أي تعديل.',
               'After finalising: the task moves to "Closed & audited" and is locked against any edits.')}
          </div>
        </div>
      )}

      {/* Guidance when task is being worked on (not ready yet) */}
      {task.target_type === 'agent' && !isFinalised && !isReadyForAudit && (
        <div className="mt-6 text-[11px] text-white/40 text-center bg-white/[0.02] border border-white/10 rounded-lg p-3">
          {t('* لنقل المهمة إلى قائمة "المهام المنجزة والجاهزة للتدقيق"، فعّل مرحلة "أغلق الطلب" في تبويب الطلب.',
             '* To move this task to "Ready for audit", mark the "Closed" stage as done in the Request tab.')}
        </div>
      )}
    </div>
  );
}


function InfoRow({ label, value }) {
  return (
    <div>
      <div className="text-[11px] text-white/40 mb-0.5">{label}</div>
      <div className="text-sm text-white/80">{value}</div>
    </div>
  );
}


// ── Movement tab ────────────────────────────────────────────────
// Builds a chronological timeline showing every executor the task moved
// through: initial assignment → transfers via actions → final closer.
function MovementTab({ task, accent, t, isAr }) {
  const events = [];
  const created = task.created_at || null;
  const initialAssignee = task.assigned_to_name || task.assigned_to_email;
  if (initialAssignee) {
    events.push({
      type:     'assigned',
      at:       created,
      by:       task.created_by || 'admin',
      to_name:  initialAssignee,
      to_email: task.assigned_to_email,
    });
  }
  (task.actions || []).forEach(a => {
    if (a.transfer_to_id || a.transfer_to_name || a.transfer_to_email) {
      events.push({
        type:        'transferred',
        at:          a.created_at,
        by_name:     a.created_by_name,
        by_email:    a.created_by_email,
        to_name:     a.transfer_to_name,
        to_email:    a.transfer_to_email,
        description: a.description,
      });
    }
  });
  if (task.review_status === 'closed') {
    events.push({
      type: 'closed',
      at:   task.review_closed_at,
      by:   task.review_closed_by || 'admin',
    });
  } else if (task.review_status === 'ready_for_review') {
    events.push({
      type: 'ready_for_review',
      at:   task.review_submitted_at,
      by:   task.review_submitted_by || 'admin',
    });
  }

  const currentAssignee = task.assigned_to_name || task.assigned_to_email || '—';
  const finalCloser     = task.review_closed_by || null;
  const fmt = (iso) => (iso ? new Date(iso).toLocaleString(isAr ? 'ar-EG' : 'en-GB') : '');

  return (
    <div className="space-y-4">
      {/* Summary card */}
      <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <SummaryCell icon={User} colour="#3b82f6"
                       label={t('الحائز الحالي', 'Current holder')}
                       value={currentAssignee} />
          <SummaryCell icon={ArrowRightLeft} colour="#8b5cf6"
                       label={t('عدد التحويلات', 'Transfers')}
                       value={events.filter(e => e.type === 'transferred').length} />
          <SummaryCell icon={Lock} colour={finalCloser ? '#10b981' : '#6b7280'}
                       label={t('الإغلاق النهائي', 'Final closer')}
                       value={finalCloser || t('لم يُغلَق بعد', 'Not closed yet')} />
        </div>
      </div>

      {/* Timeline */}
      <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <GitBranch className="w-4 h-4 text-white/50" />
          <h2 className="text-white font-bold text-sm">
            {t('مسار حركة الطلب بين الموظفين', 'Task movement path between staff')}
          </h2>
        </div>

        {events.length === 0 ? (
          <div className="text-center py-8 text-white/40 text-sm">
            {t('لا حركة حتى الآن.', 'No movement yet.')}
          </div>
        ) : (
          <ol className="relative ps-6">
            <span className="absolute inset-y-0 start-2 w-px bg-white/10" aria-hidden />
            {events.map((ev, idx) => {
              let Icon = ArrowRightLeft;
              let iconColour = '#8b5cf6';
              let label = t('تحويل', 'Transferred');
              if (ev.type === 'assigned')          { Icon = Users;          iconColour = '#3b82f6'; label = t('إسناد أولي', 'Initial assignment'); }
              if (ev.type === 'ready_for_review')  { Icon = ClipboardCheck; iconColour = '#d4af37'; label = t('أُرسلت للتدقيق', 'Sent for audit'); }
              if (ev.type === 'closed')            { Icon = Lock;           iconColour = '#10b981'; label = t('إغلاق نهائي', 'Final closure'); }
              return (
                <li key={idx} className="relative pb-5 last:pb-0"
                    data-testid={`movement-event-${idx}`}>
                  <span className="absolute -start-6 top-0 w-4 h-4 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: `${iconColour}25`, border: `1.5px solid ${iconColour}` }}>
                    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: iconColour }} />
                  </span>

                  <div className="bg-white/[0.02] border border-white/10 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <Icon className="w-3.5 h-3.5" style={{ color: iconColour }} />
                      <span className="text-xs font-bold" style={{ color: iconColour }}>{label}</span>
                      <span className="text-[11px] text-white/40 ms-auto">{fmt(ev.at)}</span>
                    </div>
                    {ev.type === 'assigned' && (
                      <div className="text-sm text-white/80">
                        {t('أُسندت المهمة إلى', 'Task assigned to')}{' '}
                        <span className="font-bold text-white">{ev.to_name || ev.to_email}</span>
                        {ev.by && <span className="text-xs text-white/40 ms-2">({t('بواسطة', 'by')} {ev.by})</span>}
                      </div>
                    )}
                    {ev.type === 'transferred' && (
                      <>
                        <div className="text-sm text-white/80">
                          <span className="text-white/60">{ev.by_name || ev.by_email}</span>
                          <span className="mx-2 text-white/40">→</span>
                          <span className="font-bold text-white">{ev.to_name || ev.to_email}</span>
                        </div>
                        {ev.description && (
                          <div className="text-[11px] text-white/40 mt-1 italic line-clamp-1">
                            &ldquo;{ev.description}&rdquo;
                          </div>
                        )}
                      </>
                    )}
                    {ev.type === 'ready_for_review' && (
                      <div className="text-sm text-white/80">
                        {t('أرسلها للتدقيق', 'Submitted for audit by')}{' '}
                        <span className="font-bold text-white">{ev.by}</span>
                      </div>
                    )}
                    {ev.type === 'closed' && (
                      <div className="text-sm text-emerald-300 font-semibold">
                        {t('أُغلقت المهمة نهائياً بواسطة', 'Task finalised by')}{' '}
                        <span className="text-white">{ev.by}</span>
                        <CheckCircle className="w-4 h-4 inline-block ms-1" />
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </div>

      <div className="bg-white/[0.02] border border-white/10 rounded-lg p-3 text-[11px] text-white/40 text-center">
        {t('هذا التبويب للعرض فقط. التحويلات تُسجَّل تلقائياً من تبويب "الإجراءات" عبر خيار "تحويل للموظف التالي".',
           'This tab is read-only. Transfers are logged automatically from the "Actions" tab via the "Transfer to next employee" option.')}
      </div>
    </div>
  );
}


function SummaryCell({ icon: Icon, colour, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
           style={{ backgroundColor: `${colour}15`, border: `1px solid ${colour}55` }}>
        <Icon className="w-5 h-5" style={{ color: colour }} />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] text-white/40 uppercase tracking-wide">{label}</div>
        <div className="text-sm font-semibold text-white truncate">{value}</div>
      </div>
    </div>
  );
}


// ── AI conversation card ──────────────────────────────────────
// Renders the transcript of the agent<>AI chat as an expandable card
// at the top of the Request tab (only when task.source === 'ai_generated').
function AIConversationCard({ conversation, agentId, t, isAr }) {
  const [open, setOpen] = useState(false);
  const turns = conversation || [];
  const previewText = turns.find(x => x.role === 'user')?.content?.slice(0, 90) || '';

  return (
    <div className="bg-gradient-to-br from-purple-500/10 to-transparent border border-purple-500/30 rounded-2xl overflow-hidden"
         data-testid="ai-conversation-card">
      <button onClick={() => setOpen(o => !o)}
              className="w-full p-4 flex items-center gap-3 text-start hover:bg-white/[0.02] transition">
        <div className="w-10 h-10 rounded-xl bg-purple-500/20 border border-purple-500/50 flex items-center justify-center flex-shrink-0">
          <Bot className="w-5 h-5 text-purple-300" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-bold text-white">
              {t('سجل المحادثة مع الذكاء الاصطناعي', 'Conversation with the AI assistant')}
            </h3>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-500/25 text-purple-200 font-mono">
              {turns.length} {t('رسالة', 'msgs')}
            </span>
            {agentId && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-white/50 font-mono truncate max-w-[180px]" dir="ltr">
                {agentId}
              </span>
            )}
          </div>
          {!open && previewText && (
            <div className="text-xs text-white/50 mt-1 truncate">
              {t('أول رسالة من الوكيل', 'Agent first message')}: &ldquo;{previewText}&rdquo;
            </div>
          )}
        </div>
        {open
          ? <ChevronUp   className="w-5 h-5 text-white/50 flex-shrink-0" />
          : <ChevronDown className="w-5 h-5 text-white/50 flex-shrink-0" />}
      </button>

      {open && (
        <div className="border-t border-white/10 p-4 space-y-3 max-h-[420px] overflow-y-auto bg-black/20">
          {turns.map((turn, idx) => {
            const isUser = turn.role === 'user';
            const isSystem = turn.role === 'system';
            return (
              <div key={idx}
                   className={`flex items-start gap-2 ${isUser ? 'flex-row-reverse' : ''}`}
                   data-testid={`ai-conv-turn-${idx}`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                  isUser ? 'bg-blue-500/20 border border-blue-500/40'
                         : isSystem ? 'bg-white/5 border border-white/10'
                                    : 'bg-purple-500/20 border border-purple-500/40'
                }`}>
                  {isUser ? <User className="w-3.5 h-3.5 text-blue-300" />
                          : isSystem ? <MessageCircle className="w-3.5 h-3.5 text-white/50" />
                                     : <Bot className="w-3.5 h-3.5 text-purple-300" />}
                </div>
                <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${
                  isUser ? 'bg-blue-500/15 border border-blue-500/30 text-white'
                         : isSystem ? 'bg-white/5 border border-white/10 text-white/60 text-xs italic'
                                    : 'bg-purple-500/10 border border-purple-500/30 text-white/90'
                }`}>
                  <div className="whitespace-pre-wrap break-words">{turn.content}</div>
                  {turn.at && (
                    <div className="text-[10px] text-white/40 mt-1">
                      {new Date(turn.at).toLocaleString(isAr ? 'ar-EG' : 'en-GB')}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}



// Feb 2026 (v2) — Prominent banner for the executing staff, so they know at a
// glance whether the customer has already paid the government fees (full
// package) or they still need to collect that on top.
function FeeModelBanner({ task, t, isAr }) {
  const fm = task.fee_model || 'service_only';
  const isPlusGov = fm === 'service_plus_gov';
  // Feb 2026 (iter-183) — gov total now comes from actions with has_fee=true
  // (the user's spec: "الرسوم الحكومية من وين بناخذها؟ من الإجراءات").
  const govTotal =
    (task.actions || []).filter(a => a.has_fee).reduce((s, a) => s + (Number(a.amount) || 0), 0)
    || (task.gov_procedures || []).reduce((s, r) => s + (Number(r.fee) || 0), 0);   // fallback for legacy tasks
  const serviceFee = Number(task.service_fee || 0);
  const totalDue = isPlusGov ? govTotal + serviceFee : serviceFee;

  const color = isPlusGov ? 'emerald' : 'amber';
  return (
    <div
      dir={isAr ? 'rtl' : 'ltr'}
      className={`mb-5 rounded-2xl p-4 border flex items-start gap-4 flex-wrap
        ${isPlusGov
          ? 'bg-emerald-500/10 border-emerald-500/40'
          : 'bg-amber-500/10 border-amber-500/40'}`}
      data-testid={`fee-model-banner-${fm}`}
    >
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0
        ${isPlusGov ? 'bg-emerald-500/20 text-emerald-300' : 'bg-amber-500/20 text-amber-200'}`}>
        <span className="text-lg font-bold">
          {isPlusGov ? '✓' : '!'}
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <div className={`font-bold text-sm ${isPlusGov ? 'text-emerald-300' : 'text-amber-200'}`}>
          {isPlusGov
            ? t('بكج كامل — العميل دفع كل شي (خدمة + رسوم حكومية)', 'Full package — customer paid everything (service + government fees)')
            : t('رسوم خدمة فقط — الرسوم الحكومية بتنجمع من العميل عند تنفيذ الإجراء', 'Service fee only — collect the government fees from the customer at execution time')}
        </div>
        <div className={`text-xs mt-1 ${isPlusGov ? 'text-emerald-200/80' : 'text-amber-100/80'}`}>
          {isPlusGov
            ? t('نفّذ الإجراءات على حساب الشركة ثم قم بتحصيل رسومك من الشركة.', "Execute the procedures using the company's funds — you'll be reimbursed by the company.")
            : t('اطلب الرسوم الحكومية من العميل قبل أو أثناء تنفيذ الإجراء.', 'Ask the customer for the government fees before or during execution.')}
        </div>

        {/* Breakdown */}
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2 text-[11px]">
          <div className="bg-black/30 rounded-md px-3 py-2">
            <div className="text-white/50">{t('الرسوم الحكومية', 'Government fees')}</div>
            <div className="text-white font-bold text-sm mt-0.5 font-mono" data-testid="banner-gov-total">
              <DirhamPrice value={govTotal} decimals={2} size="sm" />
            </div>
          </div>
          <div className="bg-black/30 rounded-md px-3 py-2">
            <div className="text-white/50">{t('رسوم الخدمة', 'Service fee')}</div>
            <div className="text-white font-bold text-sm mt-0.5 font-mono" data-testid="banner-service-fee">
              <DirhamPrice value={serviceFee} decimals={2} size="sm" />
            </div>
          </div>
          <div className={`rounded-md px-3 py-2 bg-${color}-500/20 border border-${color}-500/40`}>
            <div className={`text-${color}-300`}>
              {t('المستحق على العميل', 'Total from customer')}
            </div>
            <div className={`text-${color}-200 font-bold text-sm mt-0.5 font-mono`} data-testid="banner-total-due">
              <DirhamPrice value={totalDue} decimals={2} size="sm" />
            </div>
          </div>
        </div>

        {/* Procedures list — Feb 2026 (iter-183): now sourced from
             actions with has_fee=true, falling back to legacy gov_procedures. */}
        {((task.actions || []).some(a => a.has_fee) || (task.gov_procedures || []).length > 0) && (
          <details className="mt-3">
            <summary className="text-[11px] text-white/60 cursor-pointer hover:text-white">
              {t('عرض تفصيل الرسوم الحكومية', 'Show government fees breakdown')}
            </summary>
            <div className="mt-2 space-y-1">
              {(task.actions || []).filter(a => a.has_fee).map((a, i) => (
                <div key={`ac-${i}`} className="flex items-center justify-between bg-black/30 rounded-md px-3 py-1.5 text-[12px]"
                     data-testid={`banner-action-fee-${i}`}>
                  <span className="text-white/80 truncate">{a.description || '—'}</span>
                  <span className="text-white font-mono"><DirhamPrice value={a.amount} decimals={2} size="sm" /></span>
                </div>
              ))}
              {(task.gov_procedures || []).map((p, i) => (
                <div key={`gp-${i}`} className="flex items-center justify-between bg-black/30 rounded-md px-3 py-1.5 text-[12px]"
                     data-testid={`banner-procedure-${i}`}>
                  <span className="text-white/80">{p.name_ar || p.name || '—'}</span>
                  <span className="text-white font-mono"><DirhamPrice value={p.fee} decimals={2} size="sm" /></span>
                </div>
              ))}
            </div>
          </details>
        )}
      </div>
    </div>
  );
}


// Feb 2026 (iter-188) — RequestorCard
// Standalone card between "Request Details" and "Attachments" — AGENT tasks only.
// Shows the identity of the person who submitted this request on behalf of the
// target agent (captured optionally on the creation form).
function RequestorCard({ task, t, isAr }) {
  const rows = [
    { label: t('الاسم الكامل', 'Full name'), value: task.requestor_name, Icon: User },
    { label: t('البريد الإلكتروني', 'Email'), value: task.requestor_email, Icon: MessageCircle, ltr: true },
    { label: t('رقم الهاتف', 'Phone'),        value: task.requestor_phone, Icon: MessageCircle, ltr: true },
  ].filter(r => (r.value || '').toString().trim().length > 0);

  return (
    <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-5" data-testid="requestor-card">
      <h2 className="text-white font-bold text-sm mb-3 inline-flex items-center gap-2">
        <User className="w-4 h-4 text-[#d4af37]" />
        {t('بيانات المقدم لأجل هذا الطلب', 'Requestor details')}
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {rows.map((r, i) => {
          const RowIcon = r.Icon;
          return (
            <div key={i}
                 className="flex items-center gap-2.5 bg-white/[0.02] border border-white/10 rounded-lg p-3">
              <div className="w-8 h-8 rounded-md bg-[#d4af37]/10 flex items-center justify-center flex-shrink-0">
                <RowIcon className="w-4 h-4 text-[#d4af37]" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-white/40 font-semibold">
                  {r.label}
                </div>
                <div className="text-sm text-white truncate" dir={r.ltr ? 'ltr' : undefined}
                     data-testid={`requestor-card-value-${i}`}>
                  {r.value}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// Feb 2026 (iter-187) — AttachmentsCard
// Standalone card between "Request Details" and "Service Fees". Merges the
// original attachments (uploaded at creation) with attachments added later
// inside Actions/Procedures. Per user spec, action-attachments STILL show
// up inside the Actions tab too — this card just gives admins a single
// place to browse everything without leaving the Request tab.
function AttachmentsCard({ task, t, isAr }) {
  const original = (task.attachments || []).map(a => ({ ...a, _source: 'original' }));
  const fromActions = [];
  (task.actions || []).forEach((ac, ai) => {
    (ac.attachments || []).forEach((att) => {
      fromActions.push({
        ...att,
        _source:     'action',
        _actionIdx:  ai + 1,
        _actionDesc: ac.description || '',
      });
    });
  });
  const combined = [...original, ...fromActions];

  return (
    <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-5" data-testid="attachments-card">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h2 className="text-white font-bold text-sm inline-flex items-center gap-2">
          <Paperclip className="w-4 h-4 text-[#d4af37]" />
          {t('المرفقات', 'Attachments')}
          <span className="text-xs text-white/40 font-normal">({combined.length})</span>
        </h2>
        {fromActions.length > 0 && (
          <div className="text-[10px] text-white/40 flex items-center gap-2">
            <span className="inline-flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-white/30" />
              {t(`${original.length} من الطلب الأصلي`, `${original.length} original`)}
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-purple-400" />
              {t(`${fromActions.length} من الإجراءات`, `${fromActions.length} from actions`)}
            </span>
          </div>
        )}
      </div>

      {combined.length === 0 ? (
        <div className="text-center py-6 text-white/30 text-xs">
          <Paperclip className="w-6 h-6 mx-auto mb-2 opacity-40" />
          {t('لا توجد مرفقات لهذا الطلب حالياً', 'No attachments on this request yet')}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {combined.map((a, idx) => (
            <button key={idx}
               type="button"
               onClick={(e) => { e.preventDefault(); e.stopPropagation(); openAttachment(a); }}
               className="flex items-center gap-2 bg-white/[0.02] border border-white/10 rounded-lg p-2.5 hover:bg-white/5 hover:border-white/20 transition text-start"
               data-testid={`att-card-item-${idx}`}
               title={a._source === 'action'
                 ? `${t('من الإجراء', 'From action')} #${a._actionIdx}: ${a._actionDesc}`
                 : t('من الطلب الأصلي', 'Original attachment')}>
              <div className="w-8 h-8 rounded-md bg-black/40 flex items-center justify-center flex-shrink-0">
                {(a.mime_type || '').startsWith('image/')
                  ? <ImageIcon className="w-4 h-4 text-blue-300" />
                  : <FileText className="w-4 h-4 text-red-300" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs text-white/85 truncate">{a.label || a.name}</div>
                <div className="text-[10px] text-white/40 flex items-center gap-1 mt-0.5">
                  {a._source === 'action' ? (
                    <span className="text-purple-300">
                      {t(`إجراء #${a._actionIdx}`, `Action #${a._actionIdx}`)}
                    </span>
                  ) : (
                    <span>{t('من الطلب الأصلي', 'Original')}</span>
                  )}
                  {a.size ? <span>· {(a.size / 1024).toFixed(0)}KB</span> : null}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


// Feb 2026 (iter-183) — ServiceFeesCard
// Sits between "تفاصيل الطلب" and "مراحل الطلب" in the RequestTab.
//   • Two-radio: "بكج كامل" (رسوم الخدمة + الرسوم الحكومية) / "رسوم الخدمة فقط"
//   • رسم الخدمة is mandatory in both models.
//   • الرسوم الحكومية = sum of actions with has_fee=true (auto).
//   • Save → PATCH /admin/assigned-tasks/{id}/fees
function ServiceFeesCard({ task, isFinalised, t, isAr }) {
  const [feeModel, setFeeModel] = useState(task.fee_model || 'service_only');
  const [serviceFee, setServiceFee] = useState(
    task.service_fee != null ? String(task.service_fee) : ''
  );
  const [saving, setSaving] = useState(false);

  // Keep local state in sync when the parent reloads the task (e.g. after adding an action).
  useEffect(() => {
    setFeeModel(task.fee_model || 'service_only');
    setServiceFee(task.service_fee != null ? String(task.service_fee) : '');
  }, [task.id, task.updated_at, task.fee_model, task.service_fee]);

  const govTotal =
    (task.actions || []).filter(a => a.has_fee).reduce((s, a) => s + (Number(a.amount) || 0), 0);
  const sf = Number(serviceFee || 0);
  const totalDue = feeModel === 'service_plus_gov' ? govTotal + sf : sf;

  const dirty =
    (task.fee_model || 'service_only') !== feeModel ||
    String(task.service_fee ?? '') !== String(serviceFee);

  const save = async () => {
    if (!serviceFee || Number(serviceFee) < 0) {
      toast.error(t('أدخل رسوم الخدمة (رقم صحيح)', 'Enter the service fee (a valid number)'));
      return;
    }
    setSaving(true);
    try {
      await axios.patch(
        `${API_URL}/api/admin/assigned-tasks/${task.id}/fees`,
        { service_fee: Number(serviceFee), fee_model: feeModel },
        { headers: getAuthHeaders() },
      );
      toast.success(t('تم حفظ الرسوم', 'Fees saved'));
      // Force a full reload so the FeeModelBanner + totals refresh.
      window.location.reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشل الحفظ', 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-5 space-y-4" data-testid="service-fees-card">
      <div className="flex items-center justify-between">
        <h2 className="text-white font-bold text-sm">{t('رسوم الخدمة', 'Service fees')}</h2>
        <span className="text-[10px] px-2 py-0.5 rounded-md bg-white/5 border border-white/10 text-white/50">
          {t('يُحدث البطاقة العلوية للعميل تلقائياً', 'Auto-updates the customer summary above')}
        </span>
      </div>

      {/* Radio: بكج كامل / رسوم الخدمة فقط */}
      <div className="space-y-2" data-testid="service-fees-model">
        <label className={`flex items-start gap-2 bg-black border border-white/10 rounded-md px-3 py-2.5 ${isFinalised ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:border-white/20'}`}>
          <input type="radio" name="fee_model_card"
                 value="service_plus_gov"
                 checked={feeModel === 'service_plus_gov'}
                 disabled={isFinalised}
                 onChange={() => setFeeModel('service_plus_gov')}
                 className="accent-emerald-400 mt-1"
                 data-testid="fees-model-plus-gov" />
          <div className="text-sm">
            <div className="text-white font-medium">{t('بكج كامل (رسوم الخدمة + الرسوم الحكومية)', 'Full package (service + government fees)')}</div>
            <div className="text-[11px] text-white/40">
              {t('العميل دفع كل شي — الشركة تسدّد الرسوم الحكومية.', 'Customer paid everything — the company covers the government fees.')}
            </div>
          </div>
        </label>
        <label className={`flex items-start gap-2 bg-black border border-white/10 rounded-md px-3 py-2.5 ${isFinalised ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:border-white/20'}`}>
          <input type="radio" name="fee_model_card"
                 value="service_only"
                 checked={feeModel === 'service_only'}
                 disabled={isFinalised}
                 onChange={() => setFeeModel('service_only')}
                 className="accent-cyan-400 mt-1"
                 data-testid="fees-model-service-only" />
          <div className="text-sm">
            <div className="text-white font-medium">{t('رسوم الخدمة فقط', 'Service fee only')}</div>
            <div className="text-[11px] text-white/40">
              {t('العميل دفع رسوم الشركة فقط — الرسوم الحكومية يدفعها بنفسه (ملاحظة داخلية).',
                 'Customer paid the service fee only — they will pay government fees themselves (internal note).')}
            </div>
          </div>
        </label>
      </div>

      {/* Service fee input — mandatory */}
      <div>
        <label className="text-xs text-white/70 mb-1 block">
          {t('رسم الخدمة', 'Service fee')} <span className="text-red-400">*</span>
        </label>
        <Input type="number" step="0.01" min="0"
               value={serviceFee}
               onChange={(e) => setServiceFee(e.target.value)}
               placeholder="150"
               disabled={isFinalised}
               className="bg-black border-white/10 text-white h-10"
               data-testid="fees-service-fee-input" />
      </div>

      {/* Gov total (readonly) + Total */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-[11px]">
        <div className="bg-black/40 rounded-md px-3 py-2">
          <div className="text-white/50">{t('الرسوم الحكومية (من الإجراءات)', 'Gov fees (from actions)')}</div>
          <div className="text-white font-bold text-sm mt-0.5 font-mono" data-testid="fees-card-gov-total">
            <DirhamPrice value={govTotal} decimals={2} size="sm" />
          </div>
        </div>
        <div className="bg-black/40 rounded-md px-3 py-2">
          <div className="text-white/50">{t('رسم الخدمة', 'Service fee')}</div>
          <div className="text-white font-bold text-sm mt-0.5 font-mono">
            <DirhamPrice value={sf} decimals={2} size="sm" />
          </div>
        </div>
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-md px-3 py-2">
          <div className="text-emerald-300">{t('المستحق على العميل', 'Total from customer')}</div>
          <div className="text-emerald-200 font-bold text-sm mt-0.5 font-mono" data-testid="fees-card-total-due">
            <DirhamPrice value={totalDue} decimals={2} size="sm" />
          </div>
        </div>
      </div>

      {/* Save */}
      {!isFinalised && (
        <Button onClick={save} disabled={saving || !dirty}
                className="w-full text-black font-bold h-10"
                style={{ backgroundColor: '#d4af37' }}
                data-testid="fees-card-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin me-2" /> : null}
          {t('حفظ الرسوم', 'Save fees')}
        </Button>
      )}
    </div>
  );
}


// Feb 2026 (iter-189) — FeesSettlementCard
// Renders under ServiceFeesCard. Lets admin record partial customer payments
// (up to 10) against the total_price. Each payment requires an AI-verified
// receipt (the same /receipts/extract endpoint used for gov-fee receipts).
// Recipient block auto-picks:
//   • target_type=customer → target_name/email/phone (the actual customer)
//   • target_type=agent    → requestor_name/email/phone (fallback: agent info)
// So the person shown on the payment record is ALWAYS the end customer.
function FeesSettlementCard({ task, isFinalised, isLocked, onReload, t, isAr }) {
  const MAX_PAYMENTS = 10;
  // iter-190 (Feb 2026) — When the `fees_collected` stage is done, the card
  // becomes read-only: no more payments can be added/removed. It behaves the
  // same as isFinalised for interactive purposes but keeps its own visual
  // distinction (amber lock banner instead of grey finalised state).
  const readOnly = isFinalised || isLocked;

  const [showAdd, setShowAdd] = useState(false);
  const [amount, setAmount]   = useState('');
  const [note, setNote]       = useState('');
  // iter-218b (Feb 2026) — Payment method dropdown. Defaults to bank_transfer.
  const [paymentMethod, setPaymentMethod] = useState('bank_transfer');
  const [receipt, setReceipt] = useState(null);    // { base64, mime, name, extracted:{amount,date,ref}, verified }
  const [verifying, setVerifying] = useState(false);
  const [saving, setSaving]   = useState(false);

  const payments  = task.payments || [];
  const paidTotal = payments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const totalDue  = Number(task.total_price || 0);
  // iter-217c-v2 (Feb 2026) — Split into `shortfall` (المدفوع أقل من المستحق)
  // and `excess` (المدفوع أكثر من المستحق). The old `remaining` conflated
  // both. Overpayment MUST still be treated as fully-paid so the admin can
  // proceed to "collect fees", but with a RED note about the excess amount.
  const shortfall = Math.max(0, totalDue - paidTotal);
  const excess    = Math.max(0, paidTotal - totalDue);
  const remaining = shortfall;   // kept for backward-compat below
  const fullyPaid = totalDue > 0 && paidTotal >= totalDue - 0.005;

  // Recipient — customer for customer tasks, requestor for agent tasks.
  // iter-224 (Feb 2026) — Strict-requestor rule. For agent tasks, phone/email
  // come STRICTLY from the requestor card (بيانات المقدم لأجل هذا الطلب).
  // If a requestor field is empty, leave it empty — do NOT fall back to the
  // agent's own phone/email (that would surface the wrong contact for the
  // customer who actually paid). Name still falls back so we never show "—"
  // when the operator only recorded the requestor's name.
  const isAgent = task.target_type === 'agent';
  const recipient = {
    name:  isAgent ? (task.requestor_name  || task.target_name  || '—') : (task.target_name  || '—'),
    email: isAgent ? (task.requestor_email || ''                       ) : (task.target_email || ''),
    phone: isAgent ? (task.requestor_phone || ''                       ) : (task.target_phone || ''),
  };

  const resetForm = () => {
    setAmount(''); setNote(''); setPaymentMethod('bank_transfer'); setReceipt(null); setVerifying(false);
  };

  const handleReceiptPick = (file) => {
    if (!file) return;
    if (file.size > MAX_ATT_MB * 1024 * 1024) {
      toast.error(t(`الحد ${MAX_ATT_MB} ميغا`, `Max ${MAX_ATT_MB}MB`));
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      const [prefix, base64] = String(reader.result).split(',');
      const m    = /^data:([^;]+);base64$/.exec(prefix);
      const mime = m ? m[1] : file.type;
      setReceipt({ base64, mime, name: file.name, verified: false });
      setVerifying(true);
      try {
        const res = await axios.post(
          `${API_URL}/api/admin/assigned-tasks/receipts/extract`,
          { file_base64: base64, mime },
          { headers: getAuthHeaders(), timeout: 60_000 },
        );
        const data = res.data?.data || {};
        if (!data.is_receipt) {
          setReceipt(null);
          const reason = data.reject_reason || t('الملف لا يحتوي على بيانات إيصال', 'File does not contain receipt data');
          toast.error(t(`هذا الملف ليس إيصالاً — ${reason}`, `This is not a receipt — ${reason}`));
          return;
        }
        setReceipt({
          base64, mime, name: file.name, verified: true,
          extracted: {
            amount:    Number(data.amount || 0),
            date:      data.date || '',
            reference: data.reference || '',
          },
        });
        // Auto-fill amount if empty and AI returned one.
        if (!amount && data.amount > 0) setAmount(String(data.amount));
        toast.success(t(`تم التحقق من الإيصال (${data.amount || 0})`, `Receipt verified (${data.amount || 0})`));
      } catch (err) {
        setReceipt(null);
        toast.error(err.response?.data?.detail || t('فشل قراءة الإيصال', 'Receipt read failed'));
      } finally {
        setVerifying(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const submitPayment = async () => {
    if (!amount || Number(amount) <= 0) {
      toast.error(t('أدخل المبلغ المدفوع', 'Enter the paid amount'));
      return;
    }
    if (!receipt?.verified) {
      toast.error(t('ارفع إيصالاً مُتحقّق منه بالذكاء', 'Upload an AI-verified receipt first'));
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/assigned-tasks/${task.id}/payments`,
        {
          amount: Number(amount),
          payment_method: paymentMethod || 'bank_transfer',
          receipt_file: receipt.base64,
          receipt_mime: receipt.mime,
          receipt_name: receipt.name,
          extracted_amount:    receipt.extracted?.amount ?? null,
          extracted_reference: receipt.extracted?.reference || '',
          extracted_date:      receipt.extracted?.date || '',
          note: note.trim() || null,
        },
        { headers: getAuthHeaders() },
      );
      toast.success(t('تم تسجيل الدفعة', 'Payment recorded'));
      resetForm();
      setShowAdd(false);
      onReload();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشل التسجيل', 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  const removePayment = async (pid) => {
    if (!window.confirm(t('حذف هذه الدفعة؟', 'Delete this payment?'))) return;
    try {
      await axios.delete(
        `${API_URL}/api/admin/assigned-tasks/${task.id}/payments/${pid}`,
        { headers: getAuthHeaders() },
      );
      toast.success(t('تم حذف الدفعة', 'Payment removed'));
      onReload();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشل الحذف', 'Delete failed'));
    }
  };

  return (
    <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-5 space-y-4" data-testid="fees-settlement-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-white font-bold text-sm inline-flex items-center gap-2">
          <Wallet className="w-4 h-4 text-[#d4af37]" />
          {t('سداد الرسوم', 'Fees settlement')}
        </h2>
        <span className="text-[10px] px-2 py-0.5 rounded-md bg-white/5 border border-white/10 text-white/50">
          {t(`${payments.length}/${MAX_PAYMENTS} دفعات`, `${payments.length}/${MAX_PAYMENTS} payments`)}
        </span>
      </div>

      {/* Recipient block — auto-picks customer info */}
      <div className="rounded-lg border border-[#d4af37]/20 bg-[#d4af37]/[0.03] p-3">
        <div className="text-[10px] uppercase tracking-wider text-[#d4af37]/70 font-semibold mb-1">
          {t('اسم العميل الذي يسدّد', 'Paying customer')}
        </div>
        <div className="text-sm text-white font-semibold">{recipient.name}</div>
        <div className="flex items-center gap-3 mt-1 text-[11px] text-white/60 flex-wrap">
          {recipient.phone && <span dir="ltr">{recipient.phone}</span>}
          {recipient.email && <span dir="ltr">{recipient.email}</span>}
        </div>
      </div>

      {/* Totals: due / paid / remaining */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-[11px]">
        <div className="bg-black/40 rounded-md px-3 py-2">
          <div className="text-white/50">{t('المبلغ الإجمالي', 'Total due')}</div>
          <div className="text-white font-bold text-sm mt-0.5" data-testid="settlement-total-due">
            {totalDue.toLocaleString()} {t('د.إ', 'AED')}
          </div>
        </div>
        <div className="bg-emerald-500/10 border border-emerald-500/25 rounded-md px-3 py-2">
          <div className="text-emerald-300">{t('المدفوع', 'Paid')}</div>
          <div className="text-emerald-200 font-bold text-sm mt-0.5" data-testid="settlement-paid-total">
            {paidTotal.toLocaleString()} {t('د.إ', 'AED')}
          </div>
        </div>
        <div className={`rounded-md px-3 py-2 ${shortfall > 0 ? 'bg-amber-500/10 border border-amber-500/25' : 'bg-emerald-500/10 border border-emerald-500/25'}`}>
          <div className={shortfall > 0 ? 'text-amber-300' : 'text-emerald-300'}>
            {shortfall > 0 ? t('المتبقي', 'Remaining') : t('مسدَّد بالكامل', 'Fully paid')}
          </div>
          <div className={`font-bold text-sm mt-0.5 ${shortfall > 0 ? 'text-amber-200' : 'text-emerald-200'}`}
               data-testid="settlement-remaining">
            {shortfall > 0
              ? `${shortfall.toLocaleString()} ${t('د.إ', 'AED')}`
              : `0 ${t('د.إ', 'AED')}`}
          </div>
        </div>
      </div>

      {/* iter-217c-v2 — Excess (overpayment) alert in RED, as requested.
          The task can still be marked "fully paid" and the collect-fees
          button remains enabled, but the admin is loudly warned. */}
      {excess > 0.005 && (
        <div className="flex items-start gap-3 p-3 rounded-xl border-2 border-rose-500/60 bg-rose-500/10"
             data-testid="settlement-excess-warning">
          <AlertCircle className="w-5 h-5 text-rose-300 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-sm font-bold text-rose-200 mb-0.5">
              {t('⚠ يوجد مبلغ زيادة عن المطلوب', '⚠ Excess amount detected')}
            </div>
            <div className="text-xs text-rose-100/90">
              {t(`قرأ الذكاء الاصطناعي من الإيصال مبلغاً يزيد عن الإجمالي بمقدار `,
                 `The AI-read receipt amount exceeds the total by `)}
              <b className="font-mono" dir="ltr">{excess.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {t('د.إ', 'AED')}</b>
              {t(`. الطلب مُعامَل كـ "مسدَّد بالكامل" ويمكن تحصيل الرسوم — لكن يُرجى الرجوع للعميل لتسوية الفارق.`,
                 `. The task is treated as "fully paid" and the collect-fees action is allowed — but please reconcile the difference with the customer.`)}
            </div>
          </div>
        </div>
      )}

      {/* Payments list */}
      {payments.length > 0 && (
        <div className="space-y-1.5" data-testid="settlement-payments-list">
          {payments.map((p, i) => (
            <div key={p.id || i} className="flex items-center gap-2 bg-black/30 border border-white/10 rounded-md px-3 py-2"
                 data-testid={`settlement-payment-${i}`}>
              <div className="w-7 h-7 rounded-md bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-bold text-white">
                    {Number(p.amount || 0).toLocaleString()} {t('د.إ', 'AED')}
                  </span>
                  {/* iter-218b (Feb 2026) — Show the payment method
                      (defaults to bank_transfer for legacy rows). */}
                  {(() => {
                    const pmMap = {
                      bank_transfer: { ar: 'تحويل بنكي', en: 'Bank Transfer', tone: 'bg-blue-500/10 text-blue-300 border-blue-500/25' },
                      receipt:       { ar: 'إيصال قبض',  en: 'Receipt',       tone: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/25' },
                      link:          { ar: 'رابط دفع',   en: 'Payment Link',  tone: 'bg-purple-500/10 text-purple-300 border-purple-500/25' },
                      online:        { ar: 'دفع أونلاين', en: 'Online',       tone: 'bg-amber-500/10 text-amber-300 border-amber-500/25' },
                    };
                    const pm = pmMap[p.payment_method] || pmMap.bank_transfer;
                    return (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full border font-medium ${pm.tone}`}
                            data-testid={`settlement-payment-method-${i}`}>
                        {isAr ? pm.ar : pm.en}
                      </span>
                    );
                  })()}
                  {p.extracted_reference && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-cyan-500/10 text-cyan-300 border border-cyan-500/25 font-mono">
                      #{p.extracted_reference}
                    </span>
                  )}
                  {p.extracted_date && (
                    <span className="text-[10px] text-white/40">{p.extracted_date}</span>
                  )}
                </div>
                {p.note && <div className="text-[11px] text-white/50 mt-0.5">{p.note}</div>}
                <div className="text-[10px] text-white/40 mt-0.5">
                  {new Date(p.created_at).toLocaleString(isAr ? 'ar-EG' : 'en-GB')}
                  {p.created_by ? ` • ${p.created_by}` : ''}
                </div>
              </div>
              {p.receipt_file && (
                <a href={`data:${p.receipt_mime || 'application/octet-stream'};base64,${p.receipt_file}`}
                   target="_blank" rel="noopener noreferrer"
                   download={p.receipt_name || 'receipt'}
                   className="text-[11px] text-cyan-300 hover:text-cyan-100 inline-flex items-center gap-1 px-2 py-1 rounded bg-cyan-500/10 border border-cyan-500/25"
                   data-testid={`settlement-payment-receipt-${i}`}>
                  <FileText className="w-3.5 h-3.5" />
                  {t('عرض الإيصال', 'View')}
                </a>
              )}
              {!readOnly && (
                <button onClick={() => removePayment(p.id)}
                        className="p-1 rounded hover:bg-red-500/10 text-red-400"
                        data-testid={`settlement-payment-remove-${i}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add payment */}
      {!readOnly && !fullyPaid && payments.length < MAX_PAYMENTS && (
        showAdd ? (
          <div className="bg-black/30 border border-emerald-500/25 rounded-lg p-4 space-y-3" data-testid="settlement-add-form">
            <div className="flex items-center justify-between">
              <h3 className="text-white font-bold text-sm">{t('إضافة دفعة', 'Add payment')}</h3>
              <button onClick={() => { setShowAdd(false); resetForm(); }}
                      className="p-1 rounded hover:bg-white/5 text-white/60">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Amount */}
            <div>
              <label className="text-[10px] text-white/50 mb-1 block">
                {t('المبلغ المدفوع', 'Paid amount')} <span className="text-red-400">*</span>
              </label>
              <Input type="number" step="0.01" min="0"
                     value={amount}
                     onChange={(e) => setAmount(e.target.value)}
                     placeholder="0.00"
                     className="bg-black border-white/10 text-white h-10"
                     data-testid="settlement-amount-input" />
              {receipt?.verified && receipt.extracted?.amount != null && (
                <div className="text-[10px] text-cyan-300 mt-1">
                  {t(`قرأ الذكاء الاصطناعي: ${receipt.extracted.amount}`,
                     `AI extracted: $<DirhamPrice value={receipt.extracted.amount} decimals={2} size="sm" />`)}
                </div>
              )}
            </div>

            {/* Receipt uploader */}
            <div>
              <label className="text-[10px] text-white/50 mb-1 block">
                {t('إيصال الدفع', 'Payment receipt')} <span className="text-red-400">*</span>
              </label>
              {!receipt ? (
                <label className="inline-flex items-center gap-2 cursor-pointer px-3 py-2 rounded-md bg-black border border-dashed border-white/20 hover:border-emerald-500/50 text-xs text-white/80"
                       data-testid="settlement-receipt-picker">
                  <Paperclip className="w-3.5 h-3.5" />
                  {t('اختر ملف الإيصال', 'Choose receipt file')}
                  <input type="file" accept="image/*,application/pdf" className="hidden"
                         onChange={(e) => {
                           handleReceiptPick(e.target.files?.[0]);
                           e.target.value = '';
                         }}
                         data-testid="settlement-receipt-input" />
                </label>
              ) : (
                <div className={`flex items-center gap-2 rounded-md bg-black border px-3 py-2 ${
                  verifying ? 'border-cyan-500/40' :
                  receipt.verified ? 'border-emerald-500/40' : 'border-red-500/40'
                }`}>
                  {verifying ? (
                    <Loader2 className="w-4 h-4 animate-spin text-cyan-300" />
                  ) : receipt.verified ? (
                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-400" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-white truncate">{receipt.name}</div>
                    {verifying && <div className="text-[10px] text-cyan-300">{t('جاري التحقق…', 'Verifying…')}</div>}
                    {receipt.verified && (
                      <div className="text-[10px] text-emerald-400">
                        {t('إيصال صحيح', 'Verified receipt')}
                        {receipt.extracted?.reference && ` · #${receipt.extracted.reference}`}
                        {receipt.extracted?.date && ` · ${receipt.extracted.date}`}
                      </div>
                    )}
                  </div>
                  <button onClick={() => setReceipt(null)}
                          className="p-1 rounded hover:bg-red-500/10 text-red-400">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>

            {/* Optional note */}
            <div>
              <label className="text-[10px] text-white/50 mb-1 block">
                {t('ملاحظة (اختياري)', 'Note (optional)')}
              </label>
              <Input value={note} onChange={(e) => setNote(e.target.value)}
                     placeholder={t('مثال: دفعة مقدمة عبر التحويل', 'e.g. down payment via transfer')}
                     className="bg-black border-white/10 text-white h-9"
                     data-testid="settlement-note-input" />
            </div>

            {/* iter-218b (Feb 2026) — Payment method dropdown.
                Choices: تحويل بنكي (default) / إيصال قبض / رابط دفع / أونلاين.
                Feeds the receipt PDF via `payments[0].payment_method`. */}
            <div>
              <label className="text-[10px] text-white/50 mb-1 block">
                {t('طريقة السداد', 'Payment method')} <span className="text-red-400">*</span>
              </label>
              <select
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                className="w-full bg-black border border-white/10 text-white h-9 rounded-md px-3 text-sm"
                data-testid="settlement-payment-method"
              >
                <option value="bank_transfer">{t('تحويل بنكي', 'Bank Transfer')}</option>
                <option value="receipt">{t('إيصال قبض', 'Receipt')}</option>
                <option value="link">{t('رابط دفع', 'Payment Link')}</option>
                <option value="online">{t('دفع أونلاين', 'Online')}</option>
              </select>
            </div>

            <Button onClick={submitPayment}
                    disabled={saving || verifying || !amount || !receipt?.verified}
                    className="w-full text-black font-bold h-10 bg-[#d4af37] hover:bg-[#b8952e]"
                    data-testid="settlement-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin me-2" /> : <Plus className="w-4 h-4 me-2" />}
              {t('حفظ الدفعة', 'Save payment')}
            </Button>
          </div>
        ) : (
          <button onClick={() => setShowAdd(true)}
                  className="w-full py-3 rounded-xl border-2 border-dashed border-white/15 hover:border-emerald-500/40 hover:bg-emerald-500/[0.02] text-white/60 hover:text-emerald-300 flex items-center justify-center gap-2 transition"
                  data-testid="settlement-add-open">
            <Plus className="w-4 h-4" />
            {t('إضافة دفعة جديدة', 'Add new payment')}
          </button>
        )
      )}

      {fullyPaid && (
        <div className="text-xs px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 inline-flex items-center gap-1.5">
          <CheckCircle className="w-3.5 h-3.5" />
          {t('تم سداد كامل المبلغ', 'Full amount collected')}
        </div>
      )}

      {/* iter-190 — Lock banner shown when `fees_collected` stage is done. */}
      {isLocked && !isFinalised && (
        <div className="text-xs px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-200 inline-flex items-center gap-1.5"
             data-testid="settlement-locked-banner">
          <Lock className="w-3.5 h-3.5" />
          {t('تم تفعيل مرحلة "تحصيل الرسوم" — الكرت مقفل. أعد فتح المرحلة للتعديل.',
             'Fees-collected stage is active — card locked. Toggle the stage OFF to edit.')}
        </div>
      )}
    </div>
  );
}


// Feb 2026 (iter-183) — CommissionBox
// Renders under the `commission_collected` stage when it's toggled ON.
// Admin enters the commission % (mandatory, always empty by default even if
// the task carries a saved value — the user requested "دائماً خله أنت فاضي
// وإلزامي أنا أحطه"). System computes commission_amount = pct × total_due.
//
// iter-201 (Feb 2026) — MONEY DISPLAY & MATH:
//   All AED on this box uses FLOOR (truncation to fils) — never rounded up.
//   e.g. 16478.52 × 7% = 1153.4964 → 1153.49 (not 1153.50).
//   Both the computed amount and the displayed values use `floorAED` / `fmtAED`
//   from `utils/money.js` so backend + frontend stay in perfect sync.
function CommissionBox({ task, t, isAr, isFinalised, isLocked }) {
  const [pct, setPct] = useState('');
  const [saving, setSaving] = useState(false);
  // iter-194 (Feb 2026) — Admin picks the base scope:
  //   'service_only' → base = service_fee (default)
  //   'total'        → base = total_price (service + gov)
  // Persisted on the task so the merchant platform can mirror the same policy.
  const [scope, setScope] = useState(task.commission_scope || 'service_only');
  const readOnly = isFinalised || isLocked;

  // Compute the base + amount from the currently-selected scope.
  const sf   = Number(task.service_fee || 0);
  const govTotal = (task.actions || [])
    .filter(a => a.has_fee)
    .reduce((s, a) => s + (Number(a.amount) || 0), 0);
  const tp   = task.total_price != null ? Number(task.total_price) : (sf + govTotal);
  const base = scope === 'total' ? tp : sf;
  // iter-201 — Truncate to fils (floor) instead of banker's rounding.
  // 1153.4964 → 1153.49  (NOT 1153.50)
  const amount = pct && Number(pct) > 0
    ? floorAED((Number(pct) / 100) * base)
    : 0;

  // Pre-fill the input with the previously saved value + scope so the admin
  // doesn't lose their input when un-ticking the stage.
  useEffect(() => {
    if (task.commission_percent && !pct) setPct(String(task.commission_percent));
    if (task.commission_scope) setScope(task.commission_scope);
  }, [task.commission_percent, task.commission_scope, pct]);

  const save = async () => {
    if (!pct || Number(pct) <= 0) {
      toast.error(t('أدخل نسبة العمولة (%) — إلزامي', 'Enter the commission % — required'));
      return;
    }
    setSaving(true);
    try {
      await axios.patch(
        `${API_URL}/api/admin/assigned-tasks/${task.id}/commission`,
        { commission_percent: Number(pct), commission_base: base, commission_scope: scope },
        { headers: getAuthHeaders() },
      );
      toast.success(t('تم حفظ العمولة', 'Commission saved'));
      window.location.reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشل الحفظ', 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="bg-white/[0.02] border border-white/10 rounded-2xl p-5 space-y-3"
      dir={isAr ? 'rtl' : 'ltr'}
      data-testid="commission-box"
    >
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-white font-bold text-sm inline-flex items-center gap-2">
          <Coins className="w-4 h-4 text-amber-300" />
          {t('تحصيل عمولة الإيجنت', 'Agent commission collection')}
        </h2>
        {isLocked && !isFinalised && (
          <span className="text-[10px] px-2 py-0.5 rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-200 inline-flex items-center gap-1"
                data-testid="commission-locked-badge">
            <Lock className="w-3 h-3" />
            {t('مقفل — مرحلة العمولة مفعّلة', 'Locked — commission stage active')}
          </span>
        )}
      </div>

      {task.commission_percent && (
        <div className="text-[11px] text-amber-200/80 bg-black/30 rounded-md px-3 py-1.5">
          {t('المحفوظ سابقاً:', 'Previously saved:')}{' '}
          <span className="text-white font-mono">{task.commission_percent}%</span>{' '}
          = <span className="font-mono">{fmtAED(task.commission_amount)}</span> {t('د.إ', 'AED')}
          {' '}({t('على', 'on')}{' '}
          {task.commission_scope === 'total'
            ? t('الإجمالي', 'total')
            : t('رسوم الخدمة', 'service fee')}{': '}
          <span className="font-mono">{fmtAED(task.commission_base)}</span> {t('د.إ', 'AED')})
        </div>
      )}

      {/* iter-194 — Scope picker: service-only vs total (service + gov) */}
      <div className="bg-white/[0.03] border border-white/10 rounded-lg p-3 space-y-2"
           data-testid="commission-scope-picker">
        <div className="text-[11px] text-white/60 font-bold">
          {t('احتساب العمولة من:', 'Calculate commission from:')}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <label className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer transition
            ${scope === 'service_only'
              ? 'bg-blue-500/15 border-blue-400/50 text-blue-100'
              : 'bg-black/30 border-white/10 text-white/60 hover:border-white/25'}
            ${readOnly ? 'pointer-events-none opacity-70' : ''}`}
            data-testid="commission-scope-service-only">
            <input type="radio" name="commission-scope"
                   value="service_only"
                   checked={scope === 'service_only'}
                   onChange={() => setScope('service_only')}
                   disabled={readOnly}
                   className="accent-blue-500" />
            <div className="text-xs">
              <div className="font-bold">{t('رسوم الخدمة فقط', 'Service fee only')}</div>
              <div className="text-[10px] opacity-70 font-mono">
                <DirhamPrice value={sf} decimals={2} size="sm" />
              </div>
            </div>
          </label>
          <label className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer transition
            ${scope === 'total'
              ? 'bg-amber-500/15 border-amber-400/50 text-amber-100'
              : 'bg-black/30 border-white/10 text-white/60 hover:border-white/25'}
            ${readOnly ? 'pointer-events-none opacity-70' : ''}`}
            data-testid="commission-scope-total">
            <input type="radio" name="commission-scope"
                   value="total"
                   checked={scope === 'total'}
                   onChange={() => setScope('total')}
                   disabled={readOnly}
                   className="accent-amber-500" />
            <div className="text-xs">
              <div className="font-bold">{t('الرسم الإجمالي (خدمة + حكومي)', 'Total (service + gov)')}</div>
              <div className="text-[10px] opacity-70 font-mono">
                <DirhamPrice value={tp} decimals={2} size="sm" />
              </div>
            </div>
          </label>
        </div>
        <div className="text-[10px] text-white/50 leading-relaxed">
          {scope === 'service_only'
            ? t(
                'العمولة من رسوم الخدمة فقط — لا تُحتسب من الرسوم الحكومية.',
                'Commission from service fee only — government fees excluded.',
              )
            : t(
                'العمولة من الإجمالي (يشمل الرسوم الحكومية). استخدمه لطلبات ما فيها رسوم خدمة مستقلة (مثل تأسيس شركة).',
                'Commission from the total (includes government fees). Use when the task has no separate service fee (e.g. company formation).',
              )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[120px,1fr,auto] gap-2 items-end">
        <div>
          <label className="text-[11px] text-white/70 mb-1 block">
            {t('نسبة العمولة %', 'Commission %')} <span className="text-red-400">*</span>
          </label>
          <Input type="number" step="0.01" min="0" max="100"
                 value={pct}
                 onChange={(e) => setPct(e.target.value)}
                 placeholder="5"
                 disabled={readOnly}
                 className="bg-black border-white/10 text-white h-9"
                 data-testid="commission-percent-input" />
        </div>
        <div className="bg-black/40 rounded-md px-3 py-2">
          <div className="text-[10px] text-white/50">
            {scope === 'total'
              ? t('العمولة المحسوبة (% × الإجمالي)', 'Commission (= % × total)')
              : t('العمولة المحسوبة (% × رسوم الخدمة)', 'Commission (= % × service fee)')}
          </div>
          <div className="text-white font-bold text-lg font-mono" data-testid="commission-amount-preview">
            <DirhamPrice value={amount} decimals={2} size="sm" />
          </div>
          <div className="text-[10px] text-white/40 font-mono">
            {scope === 'total'
              ? `${t('الإجمالي:', 'Total:')} ${fmtAED(base)} ${t('د.إ', 'AED')}`
              : `${t('رسوم الخدمة:', 'Service fee:')} ${fmtAED(base)} ${t('د.إ', 'AED')}`}
          </div>
        </div>
        {!readOnly && (
          <Button onClick={save} disabled={saving || !pct}
                  className="h-10 bg-amber-500 hover:bg-amber-600 text-black font-bold"
                  data-testid="commission-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t('حفظ', 'Save')}
          </Button>
        )}
      </div>
    </div>
  );
}
